// @refresh reset
import React, { useEffect, useState, useReducer, useCallback, useMemo, useRef, useLayoutEffect } from 'react'
import { useStableCallback } from 'hooks/useStableCallback'
import {
  ITableRowInGroup,
  IViewPortColumn,
  IGroupRowCreate,
  IGroupCollapsed,
  GroupByDictionary,
  IColumnValuesCount,
  ISelectedCellState,
  ISelectedCellRange,
  ITableViewHistoryCell,
  IFillDragState,
  FillDirection,
  ISelectedCell,
  ISelectedGroupCell
} from 'components/spreadsheet/types'
import { DataContext, ViewportContext } from './DataContext'
export type { DataContextProps, ViewportContextProps } from './types'
import {
  KEY_PRESS,
  KEY_PRESSES,
  getInitialSpreadsheetState,
  INITIAL_SELECTED_CELL_RANGE,
  INITIAL_SELECTED_CELL,
  GROUP_KEY_SPLIT,
  ViewTypes
} from 'components/spreadsheet/constants/const'
import {
  getSpreadsheetData,
  getSpreadsheetRowsStream,
  mergeColumnValuesCount,
  buildSlicerRuntimeFilters,
  mergeSlicerFilters,
  createCellId,
  getRowHeightVariable,
  getRowValue,
  sortRows,
  isSortedBySortOrder,
  convertRowDataToNameValueMap,
  convertNameMapToRowDataArray,
  getMissingColumns,
  calculateHiddenColumnsFromParent,
  rowDataArrayToObject,
  isNonDataRow,
  isDataRow,
  findNearestDataRow
} from 'components/spreadsheet/helpers/functions'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import {
  PROGRESS_DISPATCH_INTERVAL_MS,
  PROGRESSIVE_RENDER_INTERVAL_MS,
  canRenderRowsProgressively
} from 'components/spreadsheet/helpers/progressive-render'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { ROW_NUMBER_COLUMN_WIDTH, HEADER_HEIGHT, STATS_HEIGHT, SMALL_ROW_HEIGHT } from 'style/constants'
import {
  ITableColumn,
  ITableRow,
  ITableViewColumn,
  ICellValue,
  IProcessObject,
  IContextMenuState,
  IRowsUpdatedSocket,
  ITableViewColour,
  ITableViewCell,
  IUpdateTableViewCell,
  AllowedDateFormats,
  AllowedDatetimeFormats
} from 'types'
import { spreadsheetDataReducer } from 'components/spreadsheet/contexts/data/reducer'
import { PERMISSIONS } from 'helpers/auth'
import { updateTableViewCellsAction } from 'components/spreadsheet/contexts/data/actions'
import { runSpreadsheetMutation, LARGE_TABLE_ROW_THRESHOLD } from 'components/spreadsheet/helpers/busy-overlay'
import { useRowsViewport } from 'components/spreadsheet/hooks/useRowsViewport'
import { useColumnsViewport } from 'components/spreadsheet/hooks/useColumnsViewport'
import { clampScrollTop } from 'components/spreadsheet/helpers/scroll'
import { computeShiftWheelScrollLeft } from 'components/spreadsheet/helpers/wheel'
import useGridLayout from 'components/spreadsheet/hooks/useGridLayout'
import { useAutoScroll } from 'components/spreadsheet/hooks/useAutoScroll'
import { useAuth } from 'hooks/auth'
import { useProject } from 'hooks/useProject'
import { useSlicers } from 'providers/slicers'
import { useApplicationStore } from 'hooks/application'
import { filterRows } from 'components/spreadsheet/helpers/filtering'
import {
  checkValuesSame,
  reformattedPasteData,
  transformStringToCorrectFormat,
  validatePasteField
} from 'components/spreadsheet/helpers/paste'
import { isReadOnly, isValidValue } from 'components/spreadsheet/helpers/validation'
import { computeFillCells, findFillDownTargetRow } from 'components/spreadsheet/helpers/fill'
import {
  getSelectedRange as getSelectedRangeFromState,
  buildExtendedFillRange,
  buildSelectAllSelection
} from 'components/spreadsheet/helpers/selection'
import { computeColumnSelection } from 'components/spreadsheet/helpers/columnSelection'
import {
  computeRowSelection,
  extendRowSelection,
  getRowSelectionFocusId
} from 'components/spreadsheet/helpers/rowSelection'
import { getRowLockState } from 'components/spreadsheet/helpers/locking'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import useSocket from 'hooks/useSocket'
import { joinSocketRoom } from 'helpers/socketClient'
import { subscribeResourceChanges } from 'helpers/resourceChanges'
import api from 'helpers/api'
import { formatValueForCopy, tsvEscape } from 'components/spreadsheet/helpers/format'
import { useViewAdapter } from './adapters'

interface DataContextProviderProps {
  tableId: string
  tableViewId: string
  processId?: string
  processSectionId?: string
  permissionCap?: number
  children?: React.ReactNode
  process?: IProcessObject
  showToolbar?: boolean
  showAggregate?: boolean
  fitToHeight?: boolean
}

/** Rows are laid out relative to a base row that advances in steps of this many rows. */
const ROW_BASE_STEP = 100000

const CLIPBOARD_UNAVAILABLE_MESSAGE =
  "Your browser won't let Morta use the clipboard. Update your browser or try a different one."

export const DataContextProvider = (props: DataContextProviderProps) => {
  'use no memo'
  const { project, tags } = useProject()
  const { socket } = useSocket()
  const { user } = useAuth()
  const { selections: slicerSelections } = useSlicers()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  // Lazy init: getInitialSpreadsheetState runs once for the initial state
  // instead of on every render. Pairs with the first-mount RESET skip below
  // so the spreadsheet starts in the same state RESET would produce, dropping
  // a redundant dispatch (and the useDeferredValue catch-up commit it
  // triggered) from initial mount.
  const [spreadsheetData, setSpreadsheetData] = useReducer(
    spreadsheetDataReducer,
    undefined,
    getInitialSpreadsheetState
  )
  const [selectedCell, updateSelectedCell] = useState<ISelectedCellState>(INITIAL_SELECTED_CELL)
  const [selectedGroupCell, setSelectedGroupCell] = useState<ISelectedGroupCell | null>(null)
  const [selectedCellRange, updateSelectedCellRange] = useState<ISelectedCellRange>(INITIAL_SELECTED_CELL_RANGE)
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([])
  // Anchor for Shift+click contiguous-range column selection.
  const [columnSelectionAnchorId, setColumnSelectionAnchorId] = useState<string | null>(null)
  const [pendingRenameColumnId, setPendingRenameColumnId] = useState<string | null>(null)
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  // Anchor for Shift+click contiguous-range row selection (also set by SHIFT+SPACE).
  const [rowSelectionAnchorId, setRowSelectionAnchorId] = useState<string | null>(null)
  const [isFocused, updateIsFocused] = useState<boolean>(false)
  const [filterSearchRows, setFilterSearchRows] = useState<boolean>(false)
  const [isDragging, updateIsDragging] = useState<boolean>(false)
  const [fillDragState, setFillDragState] = useState<IFillDragState | null>(null)
  const isFillDraggingRef = useRef(false)

  // Track unmounting state to force hooks to release data references
  // Using a ref so hooks can check this synchronously without causing re-renders
  const isUnmountedRef = useRef(false)
  // Also track as state to force a final render with empty data before unmount
  const [isUnmounting, setIsUnmounting] = useState(false)

  // Menus
  const [filterMenu, setFilterMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [colourMenu, setColourMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  // The Group panel, here so the column menu can open it too.
  const [groupMenu, setGroupMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  // eslint-disable-next-line
  const [uniqueNumber, _] = useState<number>(Math.random())
  const [scrollLeft, setScrollLeft] = useState(0)
  // Vertical scrolling is virtual (see helpers/scroll.ts): this is the logical offset over the
  // rows' full height. The scroll container only scrolls horizontally.
  const [logicalScrollTopState, setLogicalScrollTopState] = useState(0)
  const rowsLayerRef = useRef<HTMLDivElement | null>(null)

  // Throttle horizontal scroll updates using requestAnimationFrame to reduce re-renders
  const scrollRafRef = useRef<number | null>(null)
  const pendingScrollRef = useRef<{ left: number } | null>(null)

  // Mouse-wheel tick easing (see the wheel effect).
  const wheelTargetRef = useRef<number | null>(null)
  const wheelRafRef = useRef<number | null>(null)
  // The latest committed logical offset and the range it may take, for handlers between renders.
  const logicalScrollTopRef = useRef(0)
  const maxScrollTopRef = useRef(0)
  // The committed row base offset the rows are laid out from, for moving the rows layer.
  const rowBaseOffsetRef = useRef(0)

  // The one entry point for vertical scrolling: wheel, touch, keyboard, scrollbar and
  // scroll-into-view all come here. The rows layer is moved straight away so the frame that
  // shows the scroll shows the rows in place even when React renders a frame later; the state
  // update then fills in rows at the edges of the window.
  const scrollToLogical = useCallback((scrollTop: number): void => {
    const clamped = clampScrollTop(scrollTop, maxScrollTopRef.current)
    logicalScrollTopRef.current = clamped
    const layer = rowsLayerRef.current
    if (layer) layer.style.transform = `translate3d(0, ${rowBaseOffsetRef.current - clamped}px, 0)`
    setLogicalScrollTopState(clamped)
  }, [])

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    // Only horizontal scrolling is native. Focusing an element can still nudge a hidden-overflow
    // container vertically; undo that so the rows layer and the header stay where they are.
    if (event.currentTarget.scrollTop !== 0) event.currentTarget.scrollTop = 0

    const left = event.currentTarget.scrollLeft

    // Store the latest scroll position
    pendingScrollRef.current = { left }

    // Only schedule a new RAF if one isn't already pending
    if (scrollRafRef.current === null) {
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null
        if (pendingScrollRef.current) {
          setScrollLeft(pendingScrollRef.current.left)
          pendingScrollRef.current = null
        }
      })
    }
  }, [])

  // A rename or archive from the sidebar, of this table, shows here at once (the tab's title too,
  // when the table is the page). It has been saved already, so this sends nothing.
  useEffect(
    () =>
      subscribeResourceChanges('table', props.tableId, (change) => {
        if (change.change === 'archived') {
          setSpreadsheetData({ type: 'DELETE_TABLE' })
          return
        }
        setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'name', value: change.name })
        if (!props.processId && !props.processSectionId) document.title = change.name
      }),
    [props.tableId]
  )

  // Clean up scroll RAF on unmount
  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current)
      }
    }
  }, [])

  const rowHeight = getRowHeightVariable(spreadsheetData.viewDetails.rowHeight)
  // gridElement is the grid in state, so the wheel listener follows it: a form, chart, calendar or Custom View
  // replaces the grid, and switching back mounts a new one without the view's rows reloading.
  const { gridRef, gridElement, attachGrid, gridHeight, gridWidth, updateDimensions } = useGridLayout()
  const effectiveGridHeight = gridHeight || window.innerHeight

  // Height of the header plus the aggregate footer when one is shown (the footer also needs the
  // view type to support aggregates). The rows area is the grid less this.
  const showAggregate = props.showAggregate !== undefined ? props.showAggregate : true
  const footerShown = showAggregate && getViewCapabilities(spreadsheetData.viewDetails.type).rows.aggregate
  const stickyContentHeight = HEADER_HEIGHT + (footerShown ? STATS_HEIGHT : 0)

  // "Default Collapse Groups on Load" — see the layout effect that applies it further down. The
  // decision has to be made during render, not after it: if the groups were rendered expanded and
  // only collapsed by the effect, the tree chevrons would have a rotation to animate away from
  // (every icon carries `transition-all`), so the arrows would visibly swing shut on load.
  const defaultCollapseAppliedForViewRef = useRef<string | null>(null)
  const collapseAllGroupsPending =
    !spreadsheetData.loading &&
    spreadsheetData.viewDetails.collapsedGroupView &&
    defaultCollapseAppliedForViewRef.current !== spreadsheetData.viewDetails.publicId

  const viewAdapter = useViewAdapter(spreadsheetData)

  const { effectiveRows, effectiveTotalRows, effectiveColumnIdToIndex, effectiveIndexToColumnId, effectiveColumns } =
    viewAdapter

  const {
    rows,
    logicalTotalHeight,
    logicalScrollTop,
    maxScrollTop,
    startRowIndex,
    endRowIndex,
    groupedRows,
    expandedRowsWithGroups,
    preViewportAddRowCount,
    groupedCumulativeHeights
  } = useRowsViewport({
    gridHeight: effectiveGridHeight,
    scrollTop: logicalScrollTopState,
    rawRows: effectiveRows,
    groupBy: viewAdapter.rowsViewportOverrides.groupBy,
    totalRows: effectiveTotalRows,
    rowHeight,
    collapsedGroupIds: spreadsheetData.collapsedGroupIds,
    collapseAllGroupsPending,
    isLoading: spreadsheetData.loading,
    columns: effectiveColumns,
    isContributor: spreadsheetData.isContributor,
    disableNewRow: viewAdapter.rowsViewportOverrides.disableNewRow,
    unpackMultiselectGroupView: viewAdapter.rowsViewportOverrides.unpackMultiselectGroupView,
    treeGroupView: viewAdapter.rowsViewportOverrides.treeGroupView,
    columnIdToIndex: effectiveColumnIdToIndex,
    indexToColumnId: effectiveIndexToColumnId,
    columnStore: viewAdapter.rowsViewportOverrides.columnStore,
    isUnmountedRef,
    stickyContentHeight,
    visibleBitmap: viewAdapter.rowsViewportOverrides.visibleBitmap,
    visibleCount: viewAdapter.rowsViewportOverrides.visibleCount
  })

  // The area the rows paint in: the grid less its header and footer, or the rows themselves for
  // a short table (the footer then follows the last row).
  const rowsAreaHeight = Math.max(0, Math.min(logicalTotalHeight, effectiveGridHeight - stickyContentHeight))

  // A filtered view's rendered rows are a fresh array each call (the visible rows are picked out of
  // the bitmap), so compute them only when their inputs change: everything keyed on renderedRows,
  // the selection count among it, would otherwise redo its work on every scroll frame.
  const renderedRows = useMemo(
    () => viewAdapter.getRenderedRows(expandedRowsWithGroups, spreadsheetData),
    [expandedRowsWithGroups, spreadsheetData]
  )

  const { columns, allColumns, lastFrozenColumnIndex, totalWidth, treeColumnWidth, treeLeafColumn } =
    useColumnsViewport({
      rawColumns: effectiveColumns,
      frozenIndex: viewAdapter.columnsViewportOverrides.frozenIndex,
      rawGroupByColumns: viewAdapter.columnsViewportOverrides.rawGroupByColumns,
      hiddenColumnIds: viewAdapter.columnsViewportOverrides.hiddenColumnIds,
      scrollLeft,
      viewportWidth: gridWidth,
      treeGroupView: viewAdapter.columnsViewportOverrides.treeGroupView
    })

  const effectiveSpreadsheetData = viewAdapter.effectiveSpreadsheetData

  const inProcess = !!spreadsheetData.processId

  // Sync scroll position when grid dimensions change (e.g., due to browser zoom)
  // Also runs on initial mount to handle page load at non-100% zoom
  useLayoutEffect(() => {
    if (gridRef.current) {
      setScrollLeft(gridRef.current.scrollLeft)
    }
  }, [gridHeight])

  // Re-measure grid dimensions when loading completes
  // The grid element is conditionally rendered based on loading state,
  // so we need to measure after it becomes available
  useEffect(() => {
    if (!spreadsheetData.loading) {
      // Use requestAnimationFrame to ensure the browser has completed layout
      const rafId = requestAnimationFrame(() => {
        updateDimensions()
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [spreadsheetData.loading, updateDimensions])

  const checkSelectedCellExists = (cellId: string) => {
    const selectedCellElement = document.getElementById(cellId)
    if (selectedCellElement) return selectedCellElement
    else return null
  }

  const horizontalScroll = useCallback((left: number) => {
    if (!gridRef.current) {
      return
    }
    gridRef.current.scrollLeft = left
  }, [])

  const setSelectedCell = useCallback((selectedCell: ISelectedCellState) => {
    updateSelectedCell(selectedCell)
    // The two selections are mutually exclusive so only one outline is ever drawn.
    setSelectedGroupCell(null)
    if (selectedCell.rowId === '' && selectedCell.columnId === '') {
      setIsFocused(false)
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    } else {
      setSelectedColumnIds((prev) => (prev.length ? [] : prev))
      setColumnSelectionAnchorId(null)
    }
  }, [])

  const isFocusedRef = useRef(isFocused)
  useEffect(() => {
    isFocusedRef.current = isFocused
  }, [isFocused])

  const selectedCellRef = useRef(selectedCell)
  const selectedCellRangeRef = useRef(selectedCellRange)
  useEffect(() => {
    selectedCellRef.current = selectedCell
    selectedCellRangeRef.current = selectedCellRange
  }, [selectedCell, selectedCellRange])

  // Kept in sync to avoid stale closures in callbacks that need the latest value
  const renderedRowsRef = useRef(renderedRows)
  useEffect(() => {
    renderedRowsRef.current = renderedRows
  }, [renderedRows])

  // Reset selection when the selected row is no longer visible (filtered out or deleted)
  useEffect(() => {
    if (selectedCell.rowId === '') return
    // In transpose mode, check the transposed rows (effectiveRows) since the selected
    // cell has a synthetic row ID that won't exist in the original spreadsheetData.rows
    const rowsToCheck = viewAdapter.rowsForSelectionCheck
    const rawIdx = rowsToCheck.findIndex((r) => r.publicId === selectedCell.rowId)
    const bitmap = viewAdapter.bitmapForSelectionCheck
    // Row was deleted (not in rows at all) or filtered out (in rows but bitmap says hidden)
    if (rawIdx === -1 || (bitmap && !bitmap[rawIdx])) {
      // setSelectedCell(INITIAL) already resets cell range internally
      setSelectedCell(INITIAL_SELECTED_CELL)
    }
  }, [
    spreadsheetData.rows,
    spreadsheetData.visibleBitmap,
    viewAdapter.rowsForSelectionCheck,
    viewAdapter.bitmapForSelectionCheck
  ])

  // Same for a selected group header: the group it stands for can be filtered or deleted away.
  useEffect(() => {
    if (!selectedGroupCell) return
    if (!spreadsheetData.rows.some((row) => row.publicId === selectedGroupCell.rowId)) setSelectedGroupCell(null)
  }, [spreadsheetData.rows, selectedGroupCell])

  // Drop any selected column that no longer exists (e.g. after it was deleted) so the
  // highlight and bulk menu never reference stale column IDs.
  useEffect(() => {
    setSelectedColumnIds((prev) => {
      if (prev.length === 0) return prev
      const validIds = new Set(spreadsheetData.viewDetails.columns.map((column) => column.publicId))
      const pruned = prev.filter((id) => validIds.has(id))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [spreadsheetData.viewDetails.columns])

  const hasInitialFocusRef = useRef(false)

  const setIsFocused = useCallback((isFocused: boolean) => {
    updateIsFocused(isFocused)
    // update the ref immediately so callbacks see the latest value
    isFocusedRef.current = isFocused
  }, [])

  const setSelectedCellRange = useCallback(
    (newRange: ISelectedCellRange) => {
      // Bail out if same reference — prevents redundant renders during cascading updates
      if (newRange === selectedCellRangeRef.current) return

      // use the ref, NOT the closed-over state
      const focused = isFocusedRef.current

      if (focused) {
        selectedCellRangeRef.current = newRange
        updateSelectedCellRange(newRange)
      } else {
        // allow initial "anchor" (-1 → no anchor yet)
        if (newRange.endColumnIndex === -1) {
          selectedCellRangeRef.current = newRange
          updateSelectedCellRange(newRange)
        }
      }
    },
    [] // no deps; always read current value from the ref
  )

  // Drag tracking for auto-scroll
  const isDraggingRef = useRef(false)
  const setIsDragging = useCallback((dragging: boolean) => {
    updateIsDragging(dragging)
    isDraggingRef.current = dragging
  }, [])

  // --- Fill drag handlers ---
  const startFillDrag = useCallback(() => {
    const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
    if (treeLeafColumn) nonGroupedColumns.unshift(treeLeafColumn)
    const columnPositionMap = new Map(nonGroupedColumns.map((c, i) => [c.column.publicId, i]))

    const anchorColPos = columnPositionMap.get(selectedCellRef.current.columnId)
    if (anchorColPos === undefined) return

    const startRowIndex = selectedCellRef.current.rowNumber
    let endRowIndex = selectedCellRef.current.rowNumber
    const startColId = selectedCellRef.current.columnId
    let endColId = selectedCellRef.current.columnId

    const range = selectedCellRangeRef.current
    if (range.endRowIndex !== -1) {
      endRowIndex = range.endRowIndex
    }
    if (range.endColumnId) {
      endColId = range.endColumnId
    }

    // Normalize so start <= end for rows
    const minRow = Math.min(startRowIndex, endRowIndex)
    const maxRow = Math.max(startRowIndex, endRowIndex)

    setFillDragState({
      sourceStartRowIndex: minRow,
      sourceEndRowIndex: maxRow,
      sourceStartColId: startColId,
      sourceEndColId: endColId,
      targetRowIndex: maxRow,
      targetColId: endColId,
      direction: null
    })
    isFillDraggingRef.current = true
  }, [allColumns, treeLeafColumn])

  // Double-click fill: fill down to the extent of adjacent column data
  const fillDownToData = useCallback(() => {
    const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
    if (treeLeafColumn) nonGroupedColumns.unshift(treeLeafColumn)
    const columnPositionMap = new Map(nonGroupedColumns.map((c, i) => [c.column.publicId, i]))

    const anchorColPos = columnPositionMap.get(selectedCellRef.current.columnId)
    if (anchorColPos === undefined) return

    const startRowIndex = selectedCellRef.current.rowNumber
    let endRowIndex = selectedCellRef.current.rowNumber
    const startColId = selectedCellRef.current.columnId
    let endColId = selectedCellRef.current.columnId

    const range = selectedCellRangeRef.current
    if (range.endRowIndex !== -1) {
      endRowIndex = range.endRowIndex
    }
    if (range.endColumnId) {
      endColId = range.endColumnId
    }

    const minRow = Math.min(startRowIndex, endRowIndex)
    const maxRow = Math.max(startRowIndex, endRowIndex)

    const rows = renderedRows as ITableRow[]
    const allColumnsForFill = treeLeafColumn ? [treeLeafColumn, ...allColumns] : allColumns
    const targetRow = findFillDownTargetRow(
      maxRow,
      startColId,
      rows,
      allColumnsForFill,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnStore
    )
    if (targetRow === null) return

    const fillState: IFillDragState = {
      sourceStartRowIndex: minRow,
      sourceEndRowIndex: maxRow,
      sourceStartColId: startColId,
      sourceEndColId: endColId,
      targetRowIndex: targetRow,
      targetColId: endColId,
      direction: 'down'
    }

    const fillCells = computeFillCells(
      fillState,
      rows,
      allColumnsForFill,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnStore,
      spreadsheetData.userConfiguration.rowLockSettings
    )
    if (fillCells.length === 0) return

    // Optimistic local update
    const bulkEditCells = fillCells.map((fc) => ({
      columnId: fc.columnId,
      rowId: fc.rowId,
      value: { [fc.columnId]: fc.value }
    }))
    setSpreadsheetData({ type: 'BULK_EDIT_CELL', cells: bulkEditCells })

    // API call with history
    const apiCells = fillCells.map((fc) => ({
      columnId: fc.columnId,
      rowId: fc.rowId,
      value: fc.value,
      oldValue: fc.oldValue
    }))

    const context = {
      projectId: project.publicId,
      ...(spreadsheetData.processId
        ? {
            processPublicId: spreadsheetData.processId,
            processSectionPublicId: spreadsheetData.processSectionId!
          }
        : {})
    }

    updateTableViewCellsAction(
      spreadsheetData.viewDetails.publicId,
      { cells: apiCells, context },
      spreadsheetData,
      setSpreadsheetData,
      () => {},
      (error) => {
        displayErrorMessage(typeof error === 'string' ? error : "The cells couldn't be filled. Try again.")
      }
    )

    // Extend selection to include the filled range. buildExtendedFillRange handles the
    // single-cell source case where the previous range endpoints are unset (-1 / undefined),
    // falling back to the anchor's own column so `inSelectedCellRange` evaluates correctly.
    updateSelectedCellRange(buildExtendedFillRange(selectedCellRef.current, selectedCellRangeRef.current, targetRow))
  }, [allColumns, treeLeafColumn, renderedRows, spreadsheetData, project.publicId, displayErrorMessage])

  // Helper to find which cell is at a given screen position
  const getCellAtScreenPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!gridRef.current) return null

      const rect = gridRef.current.getBoundingClientRect()
      const currentScrollLeft = gridRef.current.scrollLeft

      // Convert to grid coordinates (account for row number column and header), using the
      // logical vertical offset since vertical scrolling is virtual.
      const rowNumberWidth = ROW_NUMBER_COLUMN_WIDTH
      const headerHeight = HEADER_HEIGHT
      const gridX = clientX - rect.left + currentScrollLeft - rowNumberWidth
      const gridY = clientY - rect.top + logicalScrollTopRef.current - headerHeight

      if (gridY < 0 || gridX < 0) return null

      // Find row index
      const rowIndex = Math.floor(gridY / rowHeight)
      const actualRows = renderedRows

      if (rowIndex < 0 || rowIndex >= actualRows.length) return null

      // Find column by iterating through columns
      let columnIndex = -1
      let columnId = ''
      const nonGroupedColumns = allColumns.filter((col) => !col.isGrouped)

      for (let i = 0; i < nonGroupedColumns.length; i++) {
        const col = nonGroupedColumns[i]
        const colLeft = col.left - rowNumberWidth
        const colRight = colLeft + col.column.width

        if (gridX >= colLeft && gridX < colRight) {
          columnIndex = i
          columnId = col.column.publicId
          break
        }
      }

      if (columnIndex === -1) return null

      return { rowIndex, columnIndex, columnId }
    },
    [gridRef, rowHeight, renderedRows, allColumns]
  )

  // Auto-scroll handlers at edge of document
  const handleAutoScroll = useCallback(
    (deltaX: number, deltaY: number) => {
      if (!gridRef.current) return

      gridRef.current.scrollLeft += deltaX
      scrollToLogical(logicalScrollTopRef.current + deltaY)
    },
    [gridRef, scrollToLogical]
  )

  // Update the selected cell range during auto-scroll
  const handleAutoScrollPositionUpdate = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDraggingRef.current) return

      const cell = getCellAtScreenPosition(clientX, clientY)
      if (cell) {
        updateSelectedCellRange({
          endRowIndex: cell.rowIndex,
          endColumnIndex: cell.columnIndex,
          endColumnId: cell.columnId
        })
      }
    },
    [getCellAtScreenPosition]
  )

  const { handleMouseMove: autoScrollMouseMove, stopAutoScroll } = useAutoScroll({
    gridRef,
    enabled: isDragging,
    onScroll: handleAutoScroll,
    onPositionUpdate: handleAutoScrollPositionUpdate
  })

  // Track fill drag start position for direction lock
  const fillDragStartRef = useRef<{ x: number; y: number } | null>(null)
  const fillDragStateRef = useRef<IFillDragState | null>(null)
  useEffect(() => {
    fillDragStateRef.current = fillDragState
  }, [fillDragState])

  // Document-level event listeners for drag selection and fill drag with auto-scroll
  useEffect(() => {
    const handleDocumentMouseMove = (event: MouseEvent) => {
      if (isFillDraggingRef.current) {
        // Determine direction on first significant move
        if (!fillDragStartRef.current) {
          fillDragStartRef.current = { x: event.clientX, y: event.clientY }
        }

        const currentState = fillDragStateRef.current
        if (!currentState) return

        // Fill drag is vertical-only, so we need the row index even when the
        // mouse is horizontally outside the column bounds (e.g. past the last column).
        // getCellAtScreenPosition returns null in that case, so compute row index directly.
        const cell = getCellAtScreenPosition(event.clientX, event.clientY)
        let rowIndex: number | null = null
        if (cell) {
          rowIndex = cell.rowIndex
        } else if (gridRef.current) {
          const rect = gridRef.current.getBoundingClientRect()
          const gridY = event.clientY - rect.top + logicalScrollTopRef.current - HEADER_HEIGHT
          if (gridY >= 0) {
            const computed = Math.floor(gridY / rowHeight)
            const actualRows = renderedRows as ITableRow[]
            if (computed >= 0 && computed < actualRows.length) {
              rowIndex = computed
            }
          }
        }
        if (rowIndex === null) return

        // Dynamically compute direction based on cursor position relative to source range
        const minSourceRow = Math.min(currentState.sourceStartRowIndex, currentState.sourceEndRowIndex)
        const maxSourceRow = Math.max(currentState.sourceStartRowIndex, currentState.sourceEndRowIndex)
        let direction: FillDirection | null = null
        if (rowIndex > maxSourceRow) {
          direction = 'down'
        } else if (rowIndex < minSourceRow) {
          direction = 'up'
        }

        // Snap target to nearest data row (skip group headers, add-rows, collapsed rows)
        const actualRows = renderedRows
        let snappedRowIndex = rowIndex
        if (direction && isNonDataRow(actualRows, rowIndex)) {
          const nearest = findNearestDataRow(actualRows, rowIndex, direction === 'down' ? 1 : -1)
          if (
            nearest === -1 ||
            (direction === 'down' && nearest <= maxSourceRow) ||
            (direction === 'up' && nearest >= minSourceRow)
          ) {
            // No valid data row in drag direction — stay within source range (no fill)
            direction = null
            snappedRowIndex = maxSourceRow
          } else {
            snappedRowIndex = nearest
          }
        }

        setFillDragState({
          ...currentState,
          direction,
          targetRowIndex: snappedRowIndex,
          targetColId: cell?.columnId ?? currentState.targetColId
        })

        autoScrollMouseMove(event)
        return
      }

      if (isDraggingRef.current) {
        autoScrollMouseMove(event)
      }
    }

    const handleDocumentMouseUp = () => {
      if (isFillDraggingRef.current) {
        isFillDraggingRef.current = false
        fillDragStartRef.current = null
        // executeFill is called via effect when fillDragState changes to null
        // We need to execute first, then clear
        const state = fillDragStateRef.current
        if (state?.direction) {
          // Execute the fill synchronously before clearing state
          const rows = renderedRows as ITableRow[]
          const allColumnsForFill = treeLeafColumn ? [treeLeafColumn, ...allColumns] : allColumns

          const fillCells = computeFillCells(
            state,
            rows,
            allColumnsForFill,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.columnStore,
            spreadsheetData.userConfiguration.rowLockSettings
          )

          if (fillCells.length > 0) {
            // Optimistic local update
            const bulkEditCells = fillCells.map((fc) => ({
              columnId: fc.columnId,
              rowId: fc.rowId,
              value: { [fc.columnId]: fc.value }
            }))
            setSpreadsheetData({ type: 'BULK_EDIT_CELL', cells: bulkEditCells })

            // API call with history
            const apiCells = fillCells.map((fc) => ({
              columnId: fc.columnId,
              rowId: fc.rowId,
              value: fc.value,
              oldValue: fc.oldValue
            }))

            const context = {
              projectId: project.publicId,
              ...(spreadsheetData.processId
                ? {
                    processPublicId: spreadsheetData.processId,
                    processSectionPublicId: spreadsheetData.processSectionId!
                  }
                : {})
            }

            // Capture selection before we extend it so undo can restore it.
            // Store references directly (no spread): when the user had a single cell selected,
            // the range ref points at the INITIAL_SELECTED_CELL_RANGE singleton, and
            // getSelectedRange() uses `===` against that singleton to decide whether to
            // synthesise a single-cell range for copy. Spreading here would break that
            // identity, causing copy after undo to include every row above the selection.
            // React state objects are replaced, not mutated, so holding references is safe.
            const prevSelectedCell = selectedCellRef.current
            const prevSelectedCellRange = selectedCellRangeRef.current

            // Extend selection to include the filled range. Use buildExtendedFillRange
            // so that a single-cell source (unset endColumnIndex/endColumnId) falls back
            // to the anchor's column — otherwise the filled cells don't highlight as
            // in-range, and redo would restore the same broken state.
            const nextSelectedCellRange = buildExtendedFillRange(
              selectedCellRef.current,
              selectedCellRangeRef.current,
              state.targetRowIndex
            )
            updateSelectedCellRange(nextSelectedCellRange)

            updateTableViewCellsAction(
              spreadsheetData.viewDetails.publicId,
              { cells: apiCells, context },
              spreadsheetData,
              setSpreadsheetData,
              () => {},
              (error) => {
                displayErrorMessage(typeof error === 'string' ? error : "The cells couldn't be filled. Try again.")
              },
              false,
              {
                prevSelectedCell,
                prevSelectedCellRange,
                nextSelectedCell: prevSelectedCell,
                nextSelectedCellRange
              }
            )
          }
        }
        setFillDragState(null)
        stopAutoScroll()
        return
      }

      if (isDraggingRef.current) {
        setIsDragging(false)
        stopAutoScroll()
      }
    }

    document.addEventListener('mousemove', handleDocumentMouseMove)

    // If we mouse up anywhere on the document, stop dragging (doesnt have to be in spreadsheet)
    document.addEventListener('mouseup', handleDocumentMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
      document.removeEventListener('mouseup', handleDocumentMouseUp)
    }
  }, [
    autoScrollMouseMove,
    stopAutoScroll,
    setIsDragging,
    getCellAtScreenPosition,
    spreadsheetData,
    allColumns,
    treeLeafColumn,
    expandedRowsWithGroups,
    displayErrorMessage,
    project.publicId
  ])

  const rowStreamAbortRef = useRef<AbortController | null>(null)
  const loadCallIdRef = useRef(0)

  const loadInitialData = async (viewId?: string) => {
    // Abort any in-flight row stream immediately so the progress bar doesn't linger
    rowStreamAbortRef.current?.abort()
    rowStreamAbortRef.current = null

    // Track this call so we can detect if a newer call has superseded it
    const callId = ++loadCallIdRef.current

    const newSpreadsheetData = await getSpreadsheetData(
      props.tableId,
      viewId || props.tableViewId,
      props.processId,
      props.process
    )

    // A newer loadInitialData call was triggered while we were awaiting metadata — bail out
    if (callId !== loadCallIdRef.current) return

    // The table or view is not shared with this user, or is gone: the grid shows that instead of
    // the rows, so there are none to request. Any other failure carries on to the row stream's
    // error, which offers AI Support.
    const { errorStatusCode } = newSpreadsheetData
    if (errorStatusCode === 403 || errorStatusCode === 404) {
      setSpreadsheetData({ type: 'FINISH_LOADING', spreadsheetData: newSpreadsheetData, columnValuesCount: {} })
      return
    }

    // If the spreadsheet is not being loaded in a process set the title
    if (!props.processId && !props.processSectionId) document.title = `${newSpreadsheetData.tableDetails.name}`

    // If the spreadsheet is being loaded within a process then table admin tools should be disallowed
    if (props.processId || props.processSectionId || (props.permissionCap && props.permissionCap < PERMISSIONS.owner)) {
      newSpreadsheetData['processId'] = props.processId
      newSpreadsheetData['processSectionId'] = props.processSectionId
      newSpreadsheetData['isAdmin'] = false
      const view_column_ids = newSpreadsheetData['viewDetails']['columns'].map(
        (column: ITableViewColumn) => column.publicId
      )
      // Colour rules are evaluated client-side only, so drop those whose column isn't visible
      // here (unchanged behaviour; the hidden-column colour case is tracked separately).
      newSpreadsheetData['userConfiguration']['colourSettings'] = newSpreadsheetData['viewDetails'][
        'colourSettings'
      ].filter((colourSetting: ITableViewColour) => view_column_ids.includes(colourSetting.columnId))
      // Row-lock rules are kept in full, including rules keyed off a column hidden from the view
      // (e.g. a join column). Dropping them here silently disabled locking in document embeds
      // rules on hidden columns are evaluated server-side and a single per-row locked
      // flag streamed on each row (row.hiddenRowLocked), so the hidden values never reach the client.
      newSpreadsheetData['userConfiguration']['rowLockSettings'] =
        newSpreadsheetData['viewDetails']['rowLockSettings'] ?? []
    } else {
      newSpreadsheetData['userConfiguration']['colourSettings'] = newSpreadsheetData['viewDetails']['colourSettings']
      newSpreadsheetData['userConfiguration']['rowLockSettings'] =
        newSpreadsheetData['viewDetails']['rowLockSettings'] ?? []
    }

    // A user is allowed to know the chart settings
    newSpreadsheetData['userConfiguration']['chartSettings'] = newSpreadsheetData['viewDetails']['chartSettings']

    // A user is allowed to know the sort settings
    newSpreadsheetData['userConfiguration']['sortSettings'] = newSpreadsheetData['viewDetails']['sortSettings']

    // If the spreadsheet has been deleted cap at read access
    if (newSpreadsheetData['tableDetails']['isDeleted']) {
      newSpreadsheetData['isContributor'] = false
      newSpreadsheetData['isAdmin'] = false
    }

    // If the spreadsheet is being loaded in a process and a permission cap is in place (as a response has been received for example) then set permissions in the s to that level
    if (props.permissionCap !== undefined) {
      newSpreadsheetData['isContributor'] =
        newSpreadsheetData['isContributor'] && props.permissionCap >= PERMISSIONS.contributor
    }

    // Store templateRender flag from process object if available
    if (props.process?.templateRender !== undefined) {
      newSpreadsheetData['templateRender'] = props.process.templateRender
    }

    // If the user is an admin copy over the view details to the user configuration
    // Or if we are in a process and the view is a personal view
    if (newSpreadsheetData['isAdmin'] || (inProcess && newSpreadsheetData.viewDetails.isPersonalView)) {
      newSpreadsheetData['userConfiguration']['filterSettings'] = newSpreadsheetData['viewDetails']['filterSettings']

      const viewColumns = newSpreadsheetData['viewDetails']['columns'].map(
        (column: ITableViewColumn) => column.publicId
      )

      let hiddenColumnIds: string[] = []
      let missingColumns: ITableViewColumn[] = []

      if (newSpreadsheetData['isAdmin']) {
        // Admin: use tableDetails.columns to find hidden columns
        hiddenColumnIds = newSpreadsheetData['tableDetails']['columns']
          .filter((column: ITableColumn) => !viewColumns.includes(column.publicId))
          .map((column: ITableColumn) => column.publicId)
        missingColumns = getMissingColumns(hiddenColumnIds, newSpreadsheetData)
      } else if (newSpreadsheetData.viewDetails.isPersonalView) {
        // Personal view in process: use configuration parent view to find hidden columns
        const configurationParentView = newSpreadsheetData['tableView'].find(
          (view) => view.publicId === newSpreadsheetData.viewDetails.configurationParentView?.publicId
        )
        if (configurationParentView) {
          const result = calculateHiddenColumnsFromParent(
            newSpreadsheetData['viewDetails']['columns'],
            configurationParentView.columns
          )

          // Also include any hidden columns already stored in the view
          const existingHiddenColumnIds = Array.isArray(newSpreadsheetData.viewDetails.hiddenColumns)
            ? newSpreadsheetData.viewDetails.hiddenColumns.map((col) => col.columnId)
            : []

          // Merge hidden column IDs (deduplicated)
          hiddenColumnIds = [...new Set([...result.hiddenColumnIds, ...existingHiddenColumnIds])]
          missingColumns = result.missingColumns
        }
      }

      newSpreadsheetData['userConfiguration']['hiddenColumns'] = hiddenColumnIds
      newSpreadsheetData['viewDetails']['columns'] = newSpreadsheetData['viewDetails']['columns'].concat(missingColumns)
      newSpreadsheetData['viewDetails']['hiddenColumns'] = missingColumns.map((col) => {
        return { columnId: col.publicId, columnName: col.name }
      })
    }
    newSpreadsheetData['userConfiguration']['groupSettings'] = newSpreadsheetData['viewDetails']['groupSettings']

    let columnValuesCount: IColumnValuesCount = {}

    // Only track value counts for columns that have duplicate validation enabled
    // This significantly reduces memory usage for large tables
    const columnsWithDuplicateValidation = new Set(
      newSpreadsheetData['viewDetails']['columns']
        .filter((col: ITableViewColumn) => col.validationNoDuplicates)
        .map((col: ITableViewColumn) => col.publicId)
    )

    // Only initialize columnValuesCount for columns that need duplicate tracking
    for (const columnId of columnsWithDuplicateValidation) {
      columnValuesCount[columnId] = {}
    }

    let renderedProgressively = false
    // How many rows the reducer has been given. After the first render, dispatches hand over only
    // the rows after this point: the grid is live from then on, so replacing its rows with the
    // stream's accumulator would undo anything the user or a socket event did to them meanwhile.
    let dispatchedRowCount = 0
    // Likewise for the duplicate-validation tallies: once the grid is live the stream's batches
    // are merged into the counts in state, which the user's edits may have changed meanwhile.
    let pendingColumnValuesCount: IColumnValuesCount = {}
    const allRows: ITableRow[] = []
    // The row count the stream says to expect; how far a stream that fails part-way got.
    let expectedRowCount = newSpreadsheetData.totalRowsOnServer
    try {
      let columnIdToIndex = new Map<string, number>()
      let indexToColumnId: string[] = []
      let columnStore: CompressedColumnStore | null = null

      const abortController = new AbortController()
      rowStreamAbortRef.current = abortController

      // Show rows as they arrive when nothing client-side will reorder or hide them afterwards.
      const progressive = canRenderRowsProgressively(newSpreadsheetData, slicerSelections, filterSearchRows)
      let lastProgressiveRenderAt = 0
      let lastProgressDispatchAt = 0

      for await (const rowsBatched of getSpreadsheetRowsStream(
        newSpreadsheetData['isAdmin'],
        props.tableId,
        props.tableViewId,
        newSpreadsheetData['isAdmin']
          ? newSpreadsheetData.tableDetails.columns
          : newSpreadsheetData.viewDetails.columns,
        newSpreadsheetData.processId,
        columnsWithDuplicateValidation,
        abortController.signal,
        newSpreadsheetData.totalRowsOnServer
      )) {
        if (renderedProgressively) mergeColumnValuesCount(pendingColumnValuesCount, rowsBatched.seenValues)
        else mergeColumnValuesCount(columnValuesCount, rowsBatched.seenValues)
        columnIdToIndex = rowsBatched.columnIdToIndex
        indexToColumnId = rowsBatched.indexToColumnId
        columnStore = rowsBatched.columnStore
        allRows.push(...rowsBatched.batchRows)

        // Use the authoritative total from the stream (exact filtered row count from the SQL query)
        // rather than totalRowsOnServer (which is the unfiltered table count fetched earlier)
        const total = rowsBatched.streamTotal ?? newSpreadsheetData.totalRowsOnServer
        expectedRowCount = total
        const progress = total > 0 ? Math.min(Math.round((allRows.length / total) * 100), 100) : 0

        if (!progressive) {
          if (total > 0) setSpreadsheetData({ type: 'SET_LOADING_PROGRESS', loadingProgress: progress })
          continue
        }

        // The reducer gets its own copies: the stream keeps appending to allRows, and state must
        // not change underneath React between dispatches.
        if (!renderedProgressively) {
          renderedProgressively = true
          lastProgressiveRenderAt = performance.now()
          lastProgressDispatchAt = lastProgressiveRenderAt
          dispatchedRowCount = allRows.length
          setSpreadsheetData({
            type: 'START_ROW_STREAM_RENDER',
            spreadsheetData: {
              ...newSpreadsheetData,
              rows: allRows.slice(),
              totalRows: allRows.length,
              columnIdToIndex,
              indexToColumnId,
              columnStore
            },
            columnValuesCount,
            loadingProgress: progress
          })
        } else if (performance.now() - lastProgressiveRenderAt >= PROGRESSIVE_RENDER_INTERVAL_MS) {
          lastProgressiveRenderAt = performance.now()
          lastProgressDispatchAt = lastProgressiveRenderAt
          const newRows = allRows.slice(dispatchedRowCount)
          dispatchedRowCount = allRows.length
          const newColumnValuesCount = pendingColumnValuesCount
          pendingColumnValuesCount = {}
          setSpreadsheetData({
            type: 'STREAM_ROWS_PROGRESS',
            rows: newRows,
            columnValuesCount: newColumnValuesCount,
            loadingProgress: progress
          })
        } else if (performance.now() - lastProgressDispatchAt >= PROGRESS_DISPATCH_INTERVAL_MS) {
          lastProgressDispatchAt = performance.now()
          setSpreadsheetData({ type: 'SET_LOADING_PROGRESS', loadingProgress: progress })
        }
      }

      newSpreadsheetData.rows = allRows
      newSpreadsheetData.totalRows = allRows.length
      newSpreadsheetData.columnIdToIndex = columnIdToIndex
      newSpreadsheetData.indexToColumnId = indexToColumnId
      newSpreadsheetData.columnStore = columnStore
    } catch (err) {
      // If the stream was aborted (user navigated away), bail out silently
      if (err instanceof DOMException && err.name === 'AbortError') return
      // Access to the view was removed, or the view deleted, while its rows loaded: show the same
      // panel as when the view itself is refused, rather than an error. Read structurally, as in
      // getErrorSupportMetadata.
      const status = (err as { rawCode?: unknown } | null)?.rawCode
      if (status === 403 || status === 404) {
        setSpreadsheetData({
          type: 'FINISH_LOADING',
          spreadsheetData: { ...newSpreadsheetData, errorStatusCode: status, rows: [], totalRows: 0 },
          columnValuesCount: {}
        })
        return
      }
      console.error('Row stream failed', err)
      if (renderedProgressively) {
        // The grid is live and may hold the user's edits: keep what has loaded and say so, rather
        // than replacing it with an empty table. The stream is finished below as a partial load.
        // The cause is hidden from the user (network or server), so offer support if a refresh doesn't fix it.
        setSnackbarMessage({
          status: 'error',
          message: `The table stopped loading after ${allRows.length.toLocaleString()} of ${expectedRowCount.toLocaleString()} rows. Refresh the page to load the rest.`,
          offerSupport: true
        })
      } else {
        // Nothing is showing yet: clear the partial state so FINISH_LOADING renders an empty grid
        // (rather than half-streamed rows) and the spinner clears.
        setSnackbarMessage({
          status: 'error',
          message: "This table's rows didn't load. Refresh the page to try again.",
          offerSupport: true
        })
        newSpreadsheetData.rows = []
        newSpreadsheetData.totalRows = 0
        newSpreadsheetData.columnIdToIndex = new Map()
        newSpreadsheetData.indexToColumnId = []
        newSpreadsheetData.columnStore = null
        columnValuesCount = {}
      }
    }

    if (renderedProgressively) {
      // The grid is already showing the rows; finish in place rather than remounting it.
      setSpreadsheetData({
        type: 'FINISH_ROW_STREAM',
        rows: allRows.slice(dispatchedRowCount),
        columnValuesCount: pendingColumnValuesCount
      })
      return
    }
    setSpreadsheetData({ type: 'FINISH_LOADING', spreadsheetData: newSpreadsheetData, columnValuesCount })
  }

  // This loads the initial table, view and row data
  const isFirstTableLoadRef = useRef(true)
  useEffect(() => {
    // Skip RESET on first mount — useReducer's lazy initializer already left us
    // in the initial state, so dispatching RESET here is a no-op for state but
    // produces a new state-object reference that triggers a re-render (and a
    // follow-up commit via useDeferredValue inside useRowsViewport). Only fire
    // RESET when tableId actually changes after mount.
    if (isFirstTableLoadRef.current) {
      isFirstTableLoadRef.current = false
    } else {
      setSpreadsheetData({ type: 'RESET', initialState: getInitialSpreadsheetState() })
    }
    hasInitialFocusRef.current = false
    if (props.tableId) loadInitialData()
  }, [props.tableId])

  // Listen for changes in the active view ID
  useEffect(() => {
    if (spreadsheetData.loading) return

    let rafId: number | null = null

    setSelectedCell(INITIAL_SELECTED_CELL)
    setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    setSelectedColumnIds([])
    setColumnSelectionAnchorId(null)
    hasInitialFocusRef.current = false
    // Reset scroll position (both vertical and horizontal)
    // This ensures column virtualization recalculates correctly when switching views
    scrollToLogical(0)
    setScrollLeft(0)
    horizontalScroll(0)

    // Force grid dimensions recalculation to ensure proper re-render
    rafId = requestAnimationFrame(() => {
      updateDimensions()
    })

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [spreadsheetData.loading, props.tableViewId, spreadsheetData.activeTableView])

  // Cleanup effect: reset state on unmount to release memory
  // This is critical for preventing memory leaks when navigating away from the spreadsheet
  // We need to clear both the reducer state AND the refs that hold data references
  // Using useLayoutEffect ensures cleanup runs synchronously before DOM removal
  useEffect(() => {
    return () => {
      // Cancel any in-flight row stream
      rowStreamAbortRef.current?.abort()
      // Signal to hooks that we're unmounting so they return empty values
      isUnmountedRef.current = true
      // Trigger state change to force re-render with empty data before unmount
      setIsUnmounting(true)
      // Reset the reducer state to release all row data references
      setSpreadsheetData({ type: 'RESET', initialState: getInitialSpreadsheetState() })
    }
  }, [])

  // Additional synchronous cleanup to ensure memory is released before component is removed
  // This runs after useEffect cleanup but before the component is unmounted from the DOM
  useLayoutEffect(() => {
    return () => {
      // Double-check the unmounting flag is set
      isUnmountedRef.current = true
    }
  }, [])

  // Handle copy/paste/cut
  const handleEvent = (event: ClipboardEvent | KeyboardEvent) => {
    return event.composedPath().find((item: EventTarget) => {
      const element = item as HTMLElement
      const inProcess = spreadsheetData.processId || spreadsheetData.processSectionId

      return (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element.id === 'table-audit-modal' ||
        element.id === 'row-comments' ||
        (element.className &&
          (element.className.includes('cell-value-editing') ||
            (element.className.includes('DraftEditor-editorContainer') && !inProcess) ||
            element.className.includes('cm-editor')))
      )
    })
  }

  const getSelectedRange = () => getSelectedRangeFromState(selectedCell, selectedCellRange)

  // allVisibleColumnIds: IDs of all non-hidden columns - used for copy/paste/delete operations
  // In tree view, treeLeafColumn is extracted from allColumns but is still visible — include it
  const allVisibleColumnIds = useMemo(() => {
    const ids = allColumns.map((column: IViewPortColumn) => column.column.publicId)
    if (treeLeafColumn) {
      ids.unshift(treeLeafColumn.column.publicId)
    }
    return ids
  }, [allColumns, treeLeafColumn])

  const currentRoomRef = useRef<string | null>(null) // tracks what we joined

  const viewIdRef = useRef(spreadsheetData.viewDetails.publicId)

  useEffect(() => {
    viewIdRef.current = spreadsheetData.viewDetails.publicId
  }, [spreadsheetData.viewDetails.publicId])

  const columnsRef = useRef(spreadsheetData.viewDetails.columns)
  useEffect(() => {
    columnsRef.current = spreadsheetData.viewDetails.columns
    // Cleanup: clear columns on unmount
    return () => {
      columnsRef.current = []
    }
  }, [spreadsheetData.viewDetails.columns])

  // The ids of the rows in this view, for the socket handler to drop updates about rows it
  // doesn't show. Built on demand from the rows ref the first time a socket update needs it and
  // dropped whenever the rows change: a Set of a million id strings costs ~80 MB and ~40 ms to
  // build, so it must not be rebuilt per render (a `useRef(new Set(...))` initialiser runs its
  // argument on every render even though only the first value is kept) nor per rows update.
  const visibleIdsRef = useRef<Set<string> | null>(null)
  const latestRowsRef = useRef(spreadsheetData.rows)
  const prevRowsLenRef = useRef(spreadsheetData.rows.length)
  useEffect(() => {
    latestRowsRef.current = spreadsheetData.rows
    const prevLen = prevRowsLenRef.current
    const nextLen = spreadsheetData.rows.length
    prevRowsLenRef.current = nextLen

    // Small deletion (e.g. delete row): keep the set. It retains a few stale IDs, which is
    // harmless — the socket handler just fetches them and gets a no-op.
    if (nextLen < prevLen && prevLen - nextLen <= 1000) {
      return
    }

    visibleIdsRef.current = null
  }, [spreadsheetData.rows])

  // Separate cleanup effect for unmount only - avoids clearing the ref between renders
  // which could cause socket updates to be dropped during the brief window
  useEffect(() => {
    return () => {
      visibleIdsRef.current = new Set()
    }
  }, [])

  const columnIdToIndexRef = useRef(spreadsheetData.columnIdToIndex)
  const indexToColumnIdRef = useRef(spreadsheetData.indexToColumnId)
  useEffect(() => {
    columnIdToIndexRef.current = spreadsheetData.columnIdToIndex
    indexToColumnIdRef.current = spreadsheetData.indexToColumnId

    return () => {
      columnIdToIndexRef.current = new Map()
      indexToColumnIdRef.current = []
    }
  }, [spreadsheetData.columnIdToIndex, spreadsheetData.indexToColumnId])

  // Refs for Page Up/Down and Ctrl+Arrow navigation
  const allRowsRef = useRef(spreadsheetData.rows)
  const expandedRowsRef = useRef(expandedRowsWithGroups)
  const isGroupedRef = useRef(false)
  const rowHeightRef = useRef(rowHeight)
  const gridHeightRef = useRef(effectiveGridHeight)
  const stickyContentHeightRef = useRef(stickyContentHeight)
  const isPageNavigatingRef = useRef(false)

  useEffect(() => {
    allRowsRef.current = renderedRows as ITableRow[]
    expandedRowsRef.current = expandedRowsWithGroups
    isGroupedRef.current = spreadsheetData.userConfiguration.groupSettings.length > 0
    rowHeightRef.current = rowHeight
    gridHeightRef.current = effectiveGridHeight
    stickyContentHeightRef.current = stickyContentHeight

    return () => {
      allRowsRef.current = []
    }
  }, [
    renderedRows,
    expandedRowsWithGroups,
    spreadsheetData.userConfiguration.groupSettings,
    rowHeight,
    effectiveGridHeight,
    stickyContentHeight
  ])

  // Wheel, trackpad and touch scrolling.
  //
  // Vertical scrolling is virtual, so the handler moves the logical offset itself: a trackpad
  // step (or momentum step) is applied 1:1 as it arrives; a mouse-wheel tick is eased over a few
  // frames towards its accumulated target, like a native smooth scroll. Horizontal movement is
  // left to the browser's native horizontal scroll. Touch pans the rows with the finger and
  // coasts after it lifts.
  useEffect(() => {
    if (spreadsheetData.loading) return

    const el = gridElement
    if (!el) return

    const LERP_FACTOR = 0.5
    const TRACKPAD_MAX_DELTA = 40

    const stopEasing = (): void => {
      wheelTargetRef.current = null
      if (wheelRafRef.current !== null) {
        cancelAnimationFrame(wheelRafRef.current)
        wheelRafRef.current = null
      }
    }

    const animate = (): void => {
      const target = wheelTargetRef.current
      if (target === null) {
        wheelRafRef.current = null
        return
      }
      const current = logicalScrollTopRef.current
      const diff = target - current
      if (Math.abs(diff) < 1) {
        scrollToLogical(target)
        wheelTargetRef.current = null
        wheelRafRef.current = null
        return
      }
      scrollToLogical(current + diff * LERP_FACTOR)
      wheelRafRef.current = requestAnimationFrame(animate)
    }

    // Input inside an editor or any scrollable element in a cell belongs to that element.
    const EDITOR_SELECTOR = 'textarea, input, select, [contenteditable="true"], .cm-editor, [data-scrollable]'
    const insideEditor = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest(EDITOR_SELECTOR) !== null

    // Whether the grid can take a vertical move of `deltaY`; at either end (or when it fits) the
    // event is left alone so the page around an embedded grid scrolls instead.
    const canScrollVertically = (deltaY: number): boolean => {
      const max = maxScrollTopRef.current
      if (max <= 0) return false
      const current = wheelTargetRef.current ?? logicalScrollTopRef.current
      return deltaY > 0 ? current < max : current > 0
    }

    const onWheel = (e: WheelEvent): void => {
      if (insideEditor(e.target)) return
      const rh = rowHeightRef.current

      // Shift + wheel = horizontal scroll (Windows/Linux mouse convention).
      // Chromium fires this with `deltaY` set and `shiftKey: true`; the browser
      // would normally convert it to horizontal scroll in its default handler,
      // but our `preventDefault()` below suppresses that, so handle it here.
      const shiftScrollLeft = computeShiftWheelScrollLeft({
        shiftKey: e.shiftKey,
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
        rowHeight: rh,
        gridHeight: gridHeightRef.current,
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth
      })
      if (shiftScrollLeft !== null) {
        e.preventDefault()
        el.scrollLeft = shiftScrollLeft
        return
      }

      // A purely horizontal gesture stays native.
      if (e.deltaY === 0) return

      // Normalise deltaY to pixels
      let deltaY = e.deltaY
      if (e.deltaMode === 1) deltaY *= rh
      else if (e.deltaMode === 2) deltaY *= gridHeightRef.current

      if (!canScrollVertically(deltaY)) return

      e.preventDefault()

      if (Math.abs(deltaY) < TRACKPAD_MAX_DELTA) {
        // Trackpad (and momentum) steps: follow the input exactly, cancelling any tick easing.
        stopEasing()
        scrollToLogical(logicalScrollTopRef.current + deltaY)
      } else {
        // Mouse-wheel tick: ease towards the accumulated target over a few frames.
        const from = wheelTargetRef.current ?? logicalScrollTopRef.current
        wheelTargetRef.current = clampScrollTop(from + deltaY, maxScrollTopRef.current)
        if (wheelRafRef.current === null) {
          wheelRafRef.current = requestAnimationFrame(animate)
        }
      }

      // Preserve horizontal scroll (the browser clamps scrollLeft itself)
      if (e.deltaX !== 0) el.scrollLeft += e.deltaX
    }

    // Touch: one finger pans both axes; after it lifts the vertical motion coasts to a stop.
    const FRICTION = 0.95
    let touch: { x: number; y: number; t: number; velocity: number } | null = null
    let coastRaf: number | null = null

    const stopCoasting = (): void => {
      if (coastRaf !== null) {
        cancelAnimationFrame(coastRaf)
        coastRaf = null
      }
    }

    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length !== 1 || insideEditor(e.target)) return
      stopEasing()
      stopCoasting()
      touch = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: e.timeStamp, velocity: 0 }
    }

    const onTouchMove = (e: TouchEvent): void => {
      if (!touch || e.touches.length !== 1) return
      const { clientX, clientY } = e.touches[0]
      const dx = touch.x - clientX
      const dy = touch.y - clientY
      const dt = Math.max(1, e.timeStamp - touch.t)
      touch = { x: clientX, y: clientY, t: e.timeStamp, velocity: dy / dt }
      // A grid that fits, or one being pushed past its end, lets the page pan instead.
      if (!canScrollVertically(dy) && Math.abs(dx) <= Math.abs(dy)) return
      e.preventDefault()
      el.scrollLeft += dx
      scrollToLogical(logicalScrollTopRef.current + dy)
    }

    const onTouchEnd = (): void => {
      if (!touch) return
      // px per ms -> px per frame, then decay
      let velocity = touch.velocity * 16
      touch = null
      if (Math.abs(velocity) < 0.5) return
      const coast = (): void => {
        scrollToLogical(logicalScrollTopRef.current + velocity)
        velocity *= FRICTION
        coastRaf = Math.abs(velocity) < 0.5 ? null : requestAnimationFrame(coast)
      }
      coastRaf = requestAnimationFrame(coast)
    }

    // Must be non-passive to allow preventDefault()
    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
      stopEasing()
      stopCoasting()
    }
  }, [spreadsheetData.loading, scrollToLogical, gridElement])

  useEffect(() => {
    const tableId = spreadsheetData.tableDetails.publicId
    const blocked = !tableId || spreadsheetData.loading || spreadsheetData.errorStatusCode !== undefined

    if (blocked) return

    let cancelJoin = () => {}
    const joinRoom = () => {
      if (!tableId) return
      cancelJoin()
      if (currentRoomRef.current && currentRoomRef.current !== tableId) {
        socket.emit('leave', currentRoomRef.current)
      }
      cancelJoin = joinSocketRoom(socket, tableId)
      currentRoomRef.current = tableId
    }

    const onConnect = () => joinRoom()
    const onDisconnect = () => {
      // socket.id is now invalid; next 'connect' will fire on re-connect
      cancelJoin()
      currentRoomRef.current = null
    }

    // join immediately if already connected
    if (socket.connected) joinRoom()

    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)

    return () => {
      cancelJoin()
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      if (currentRoomRef.current) {
        socket.emit('leave', currentRoomRef.current)
        currentRoomRef.current = null
      }
    }
  }, [socket, spreadsheetData.tableDetails.publicId, spreadsheetData.loading, spreadsheetData.errorStatusCode])

  useEffect(() => {
    const tableId = spreadsheetData.tableDetails.publicId
    const blocked =
      !socket.connected || !tableId || spreadsheetData.loading || spreadsheetData.errorStatusCode !== undefined

    if (blocked) return

    // tune these
    const CHUNK_SIZE = 150 // if GET with query params; raise if using POST body
    const WAIT_MS = 1000 // wait 1s after the last packet

    // buffer + debounce lived for this effect's lifetime
    const buffer = new Set<string>()
    let timer: ReturnType<typeof setTimeout> | null = null

    const chunk = <T,>(arr: T[], size: number): T[][] => {
      const out: T[][] = []
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
      return out
    }

    const flush = async () => {
      const ids = Array.from(buffer)
      buffer.clear()
      if (ids.length === 0) return

      // only fetch rows that are actually in this view
      if (visibleIdsRef.current === null) {
        visibleIdsRef.current = new Set(latestRowsRef.current.map((row) => String(row.publicId)))
      }
      const visibleIds = visibleIdsRef.current
      const wanted = ids.filter((id) => visibleIds.has(id))
      if (wanted.length === 0) return

      const parts = chunk(wanted, CHUNK_SIZE)
      const collected: ITableRow[] = []

      // sequential requests
      for (const p of parts) {
        try {
          const res = await api.getTableViewRows(viewIdRef.current, p)
          if (res?.data) collected.push(...res.data)
        } catch (e) {
          displayErrorMessage(e)
        }
      }

      if (collected.length) {
        const updated = collected.map((row: ITableRow) => ({
          ...row,
          rowData: convertNameMapToRowDataArray(
            columnsRef.current,
            row.rowData as unknown as Record<string, ICellValue>,
            columnIdToIndexRef.current,
            indexToColumnIdRef.current
          )
        }))
        setSpreadsheetData({ type: 'UPDATE_ROWS', rows: updated })
      }
    }

    const scheduleFlush = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(flush, WAIT_MS)
    }

    const onRowsUpdated = (data: IRowsUpdatedSocket) => {
      const incoming: string[] = data?.rowPublicIds || []
      for (const id of incoming) if (id) buffer.add(String(id)) // de-dupe
      scheduleFlush()
    }

    socket.on('rows_updated', onRowsUpdated)

    return () => {
      socket.off('rows_updated', onRowsUpdated)
      if (timer) clearTimeout(timer)
      buffer.clear() // Clear buffer to release any pending row IDs
    }
  }, [
    socket, // stable socket instance
    spreadsheetData.tableDetails.publicId, // rebind when switching tables
    spreadsheetData.loading,
    spreadsheetData.errorStatusCode
  ])

  // A single cell's value, for selections the column rectangle can't express.
  const createCopyFromSelectedCell = (groupCell?: ISelectedGroupCell): string => {
    const { columnId, rowId } = groupCell ?? selectedGroupCell ?? selectedCell
    if (!columnId || !rowId) return ''

    // Read straight off the view rather than the viewport columns: a grouped column is absent
    // from those entirely in tree group view, and present-but-unselectable otherwise.
    const column = spreadsheetData.viewDetails.columns.find((viewColumn) => viewColumn.publicId === columnId)
    if (!column) return ''

    // A collapsed group's rows aren't rendered, so fall back to the full row set.
    const selectedRow =
      (renderedRows.find((row) => isDataRow(row) && row.publicId === rowId) as ITableRow | undefined) ??
      spreadsheetData.rows.find((row) => row.publicId === rowId)
    if (!selectedRow) return ''

    const value = getRowValue(selectedRow, columnId, effectiveColumnIdToIndex, viewAdapter.columnStoreForCopy)

    return tsvEscape(formatValueForCopy(column, value))
  }

  const createCopyFromCells = () => {
    const rows = renderedRows
    const isForm = spreadsheetData.viewDetails.type === ViewTypes.FORM

    if (isForm) {
      const value = selectedCell.value
      // If you have form-specific formatting, keep it—just pass through tsvEscape
      return tsvEscape(value)
    }

    const { rowIndexes, columns: selectedColumns } = getSelectionTarget()
    if (rowIndexes.length === 0 || selectedColumns.length === 0) return createCopyFromSelectedCell()

    const outRows: string[] = []

    for (const i of rowIndexes) {
      const row = rows[i]
      // Skip non-data rows (add-row placeholder, group header, collapsed group, tree group).
      // Selection is allowed to span them, but the copied TSV stays continuous.
      if (isNonDataRow(rows, i)) continue
      const outCols: string[] = []
      for (const column of selectedColumns) {
        let value: any = null
        if (row && column && 'publicId' in row) {
          value = getRowValue(
            row as ITableRow,
            column.column.publicId,
            effectiveColumnIdToIndex,
            viewAdapter.columnStoreForCopy
          )
        }

        outCols.push(tsvEscape(formatValueForCopy(column?.column, value)))
      }
      outRows.push(outCols.join('\t'))
    }

    // Join with LF; no trailing newline
    return outRows.join('\n')
  }

  // True when text is highlighted within one ``data-copy-text`` element, so the browser's own copy
  // should win. Both ends must be in the same element, so dragging across cells copies the range.
  const hasInElementTextSelection = (): boolean => {
    const selection = window.getSelection?.()
    if (!selection || selection.isCollapsed || selection.toString() === '') return false

    const copyTextHostOf = (node: Node | null): Element | null => {
      const element = node instanceof Element ? node : (node?.parentElement ?? null)
      return element?.closest('[data-copy-text]') ?? null
    }

    const anchorHost = copyTextHostOf(selection.anchorNode)
    return anchorHost !== null && anchorHost === copyTextHostOf(selection.focusNode)
  }

  const handleCopy = (event: ClipboardEvent) => {
    if (isFocusedRef.current && !handleEvent(event) && !hasInElementTextSelection()) {
      try {
        const text = createCopyFromCells()

        if (event.clipboardData) {
          event.preventDefault() // stop the browser's default (which may add a newline)
          event.clipboardData.setData('text/plain', text)
          setSnackbarMessage({ status: 'success', message: 'Copied to clipboard' })
        } else if (navigator.clipboard?.writeText) {
          // Some environments won't expose event.clipboardData (or you may be outside a user gesture)
          event.preventDefault()
          void navigator.clipboard
            .writeText(text)
            .then(() => {
              setSnackbarMessage({ status: 'success', message: 'Copied to clipboard' })
            })
            .catch(displayErrorMessage)
        } else {
          displayErrorMessage(CLIPBOARD_UNAVAILABLE_MESSAGE)
        }
      } catch (error) {
        displayErrorMessage(error)
      }
    }
  }

  // Resolve the column slice and indices used by clear / read-only checks.
  // The columns a cell selection can span, in display order: visible, non-grouped, with the tree
  // leaf column (extracted from allColumns in tree mode) restored at the front. Every selection
  // rectangle is indexed against this list, so a whole-row selection spans its first to its last.
  const getSelectableColumns = (): IViewPortColumn[] => {
    const nonGroupedColumns = allColumns
      .filter((vColumn) => allVisibleColumnIds.includes(vColumn.column.publicId))
      .filter((column) => !column.isGrouped)

    if (treeLeafColumn && allVisibleColumnIds.includes(treeLeafColumn.column.publicId)) {
      nonGroupedColumns.unshift(treeLeafColumn)
    }

    return nonGroupedColumns
  }

  // Every column a whole-row or whole-column selection covers, in display order — grouped ones
  // included, unlike the cell rectangle. Except in tree group view, where they aren't rendered.
  const getWholeColumnOrRowSelection = (): IViewPortColumn[] => {
    const visibleColumns = allColumns.filter((vColumn) => allVisibleColumnIds.includes(vColumn.column.publicId))

    if (treeLeafColumn && allVisibleColumnIds.includes(treeLeafColumn.column.publicId)) {
      visibleColumns.unshift(treeLeafColumn)
    }

    return visibleColumns
  }

  const getSelectionColumnSlice = () => {
    const cellRange = getSelectedRange()
    const nonGroupedColumns = getSelectableColumns()

    const startColumnId = selectedCell.columnId
    const endColumnId = cellRange.endColumnId || selectedCell.columnId

    const startColIdxInAll = nonGroupedColumns.findIndex((c) => c.column.publicId === startColumnId)
    const endColIdxInAll = nonGroupedColumns.findIndex((c) => c.column.publicId === endColumnId)

    return { cellRange, nonGroupedColumns, startColIdxInAll, endColIdxInAll }
  }

  /**
   * What the current selection covers: which rows (as indexes into ``renderedRows``) and which
   * span of selectable columns. The single answer every selection-wide operation works from —
   * copy, cut, clear, and resolving rows to delete.
   *
   * A whole-row selection is authoritative when present, because Ctrl/Cmd+click can select rows
   * that aren't next to each other and no single rectangle can express that (its bounding box
   * would also cover the rows in the gaps). Such a selection therefore has no cell rectangle at
   * all — see ``selectCellsForRows`` — so anything reading the rectangle alone would silently
   * fall back to nothing. Whole rows always span every column of the view — see
   * ``getWholeColumnOrRowSelection``, which unlike the rectangle includes grouped columns.
   *
   * A whole-column selection has no rectangle either — see ``selectCellsForColumns`` — so it is
   * read straight off ``selectedColumnIds``, and spans every data row loaded into the view.
   *
   * Otherwise the cell rectangle's own row span and column span are used, which is the ordinary
   * drag / Shift+Arrow case.
   */
  const getSelectionTarget = (): { rowIndexes: number[]; columns: IViewPortColumn[] } => {
    const columns = getWholeColumnOrRowSelection()

    if (selectedRowIds.length > 0) {
      const selectedIds = new Set(selectedRowIds)
      const rowIndexes: number[] = []
      for (let i = 0; i < renderedRows.length; i++) {
        const row = renderedRows[i]
        if (row && isDataRow(row) && selectedIds.has(row.publicId)) rowIndexes.push(i)
      }
      return { rowIndexes, columns }
    }

    if (selectedColumnIds.length > 0) {
      const selectedIds = new Set(selectedColumnIds)
      const rowIndexes: number[] = []
      for (let i = 0; i < renderedRows.length; i++) {
        if (isDataRow(renderedRows[i])) rowIndexes.push(i)
      }
      // Filtering (rather than mapping over the selection) keeps display order however the user
      // built the block up, and drops hidden columns the selection can still hold.
      return { rowIndexes, columns: columns.filter((column) => selectedIds.has(column.column.publicId)) }
    }

    const { cellRange, nonGroupedColumns, startColIdxInAll, endColIdxInAll } = getSelectionColumnSlice()
    if (startColIdxInAll === -1 || endColIdxInAll === -1) return { rowIndexes: [], columns: [] }

    const startRowIndex = Math.min(selectedCell.rowNumber, cellRange.endRowIndex)
    const endRowIndex = Math.max(selectedCell.rowNumber, cellRange.endRowIndex)
    if (startRowIndex < 0 || endRowIndex < 0) return { rowIndexes: [], columns: [] }

    const rowIndexes: number[] = []
    for (let i = startRowIndex; i <= endRowIndex; i++) rowIndexes.push(i)

    const startColumnIndex = Math.min(startColIdxInAll, endColIdxInAll)
    const endColumnIndex = Math.max(startColIdxInAll, endColIdxInAll)
    return { rowIndexes, columns: nonGroupedColumns.slice(startColumnIndex, endColumnIndex + 1) }
  }

  // True if any column intersected by the current selection is read-only.
  const selectionContainsReadOnly = (): boolean => {
    const { columns: selectedColumns } = getSelectionTarget()
    if (selectedColumns.length === 0) return false

    // Transformed views (matrix, transpose) expose structurally read-only synthetic
    // columns — matrix row-labels, transpose's field column, etc. They carry synthetic
    // publicIds that isReadOnly can't see, so the adapter flags them on the effective
    // view context (only populated on the adapter's effective data, not the raw state).
    const readOnlyColumns = effectiveSpreadsheetData.viewContext?.readOnlyColumns

    for (const col of selectedColumns) {
      if (!col) continue
      if (
        isReadOnly(col, spreadsheetData.isContributor, spreadsheetData.userConfiguration.rowLockSettings) ||
        (readOnlyColumns?.has(col.column.publicId) ?? false)
      ) {
        return true
      }
    }
    return false
  }

  // Clear values in the currently selected range. Skips read-only columns with a snackbar
  // (existing Delete-key behaviour); strict refusal for cut is handled by the caller.
  const clearSelectedRange = () => {
    const rows = renderedRows
    const { rowIndexes, columns: selectedColumns } = getSelectionTarget()
    if (rowIndexes.length === 0 || selectedColumns.length === 0) return

    const newCells: ITableViewCell[] = []

    for (const i of rowIndexes) {
      const row = rows[i]
      for (const column of selectedColumns) {
        if (!row || !('publicId' in row)) {
          continue
        }

        if (
          column &&
          !isReadOnly(column, spreadsheetData.isContributor, spreadsheetData.userConfiguration.rowLockSettings)
        ) {
          const validValue = isValidValue(
            null,
            rowDataArrayToObject(
              row.rowData,
              spreadsheetData.indexToColumnId,
              row._rowIndex,
              spreadsheetData.columnStore
            ),
            spreadsheetData.columnValuesCount,
            column.column,
            undefined,
            spreadsheetData.viewDetails.type
          )

          if (validValue.error) {
            if (validValue.warn) {
              setSnackbarMessage({ status: 'warning', message: validValue.errorMessage })
              newCells.push({
                columnId: column.column.publicId,
                rowId: row.publicId,
                value: null
              })
            } else {
              setSnackbarMessage({ status: 'error', message: validValue.errorMessage })
            }
          } else {
            newCells.push({
              columnId: column.column.publicId,
              rowId: row.publicId,
              value: null
            })
          }
        } else {
          setSnackbarMessage({
            status: 'error',
            message: column
              ? `Column '${column.column.name}' is read-only, so its cells can't be cleared.`
              : "Some of the selected cells are read-only, so they can't be cleared."
          })
        }
      }
    }

    const mappedDeletePayload = viewAdapter.mapCellsForWrite({
      cells: newCells,
      context: { projectId: project.publicId }
    })
    if (!mappedDeletePayload) return
    const mappedDeleteCells = mappedDeletePayload.cells

    if (mappedDeleteCells.length > 0) {
      setSpreadsheetData({
        type: 'BULK_EDIT_CELL',
        cells: mappedDeleteCells.map((cell) => {
          return { rowId: cell.rowId, columnId: cell.columnId, value: { [cell.columnId]: cell.value } }
        })
      })

      const cellUpdates = {
        cells: mappedDeleteCells,
        context: { projectId: project.publicId }
      }

      updateTableViewCellsAction(
        spreadsheetData.viewDetails.publicId,
        cellUpdates,
        spreadsheetData,
        setSpreadsheetData,
        () => {
          return null
        },
        (error) => {
          displayErrorMessage(error)
          return null
        }
      )
    }
  }
  const handleCut = (event: ClipboardEvent) => {
    if (!isFocusedRef.current || handleEvent(event)) return

    if (selectedGroupCell) {
      event.preventDefault()
      setSnackbarMessage({
        status: 'error',
        message: "Group headers can't be cut. Copy the value instead."
      })
      return
    }

    if (selectionContainsReadOnly()) {
      event.preventDefault()
      setSnackbarMessage({
        status: 'error',
        message: "Some of the selected cells are read-only, so they can't be cut. Copy them instead."
      })
      return
    }

    try {
      const text = createCopyFromCells()

      if (event.clipboardData) {
        event.preventDefault()
        event.clipboardData.setData('text/plain', text)
        clearSelectedRange()
        setSnackbarMessage({ status: 'success', message: 'Cut to clipboard' })
      } else if (navigator.clipboard?.writeText) {
        event.preventDefault()
        void navigator.clipboard
          .writeText(text)
          .then(() => {
            clearSelectedRange()
            setSnackbarMessage({ status: 'success', message: 'Cut to clipboard' })
          })
          .catch(displayErrorMessage)
      } else {
        displayErrorMessage(CLIPBOARD_UNAVAILABLE_MESSAGE)
      }
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  // Programmatic cut entry point used by the cell context menu — bypasses the
  // execCommand path so it works regardless of focus state.
  const cutSelectedCells = async () => {
    if (selectionContainsReadOnly()) {
      setSnackbarMessage({
        status: 'error',
        message: "Some of the selected cells are read-only, so they can't be cut. Copy them instead."
      })
      return
    }

    try {
      const text = createCopyFromCells()
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        clearSelectedRange()
        setSnackbarMessage({ status: 'success', message: 'Cut to clipboard' })
      } else {
        displayErrorMessage(CLIPBOARD_UNAVAILABLE_MESSAGE)
      }
    } catch (error) {
      displayErrorMessage(error)
    }
  }

  const {
    rows: rawRows,
    isContributor,
    columnValuesCount,
    userConfiguration: { groupSettings },
    viewDetails: { type: viewType, publicId: viewId }
  } = spreadsheetData

  const handlePasteText = useCallback(
    (data: string) => {
      const runPaste = (): void => {
        // Normalize CRLF/CR to LF
        let normalized = data.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

        // Drop exactly one trailing newline that many apps place on the clipboard
        if (normalized.endsWith('\n')) normalized = normalized.slice(0, -1)

        const spreadsheetRows = renderedRows
        const cells: ITableViewCell[] = []
        const reformattedData = reformattedPasteData(normalized)
        const rows = reformattedData
        if (rows.length === 0) return

        // Use allColumns for paste so we can access columns outside the visible viewport
        // Filter by allVisibleColumnIds (all non-hidden columns), not visibleColumns (virtualized subset)
        const visibleColumns = allColumns.filter((vColumn) => allVisibleColumnIds.includes(vColumn.column.publicId))
        const nonGroupedColumns = visibleColumns.filter((column) => !column.isGrouped)

        // In tree view, the tree leaf column is extracted from allColumns
        // but participates in selection at position 0 — include it for paste
        if (treeLeafColumn && allVisibleColumnIds.includes(treeLeafColumn.column.publicId)) {
          nonGroupedColumns.unshift(treeLeafColumn)
          visibleColumns.unshift(treeLeafColumn)
        }

        // A column selection has no rectangle to paste into, so it names its columns outright — in
        // display order, and without the Ctrl/Cmd+click gaps a bounding rectangle would fill.
        // That also means it can cover a grouped column, which has no cells to build a rectangle
        // from but takes a value on every row like any other
        const selectedIds = new Set(selectedColumnIds)
        const columnSelection =
          selectedColumnIds.length > 0
            ? visibleColumns.filter((column) => selectedIds.has(column.column.publicId))
            : null
        if (columnSelection && columnSelection.length === 0) return

        // Anchoring on the first loaded row and filling to the last gives a column the same two
        // behaviours a range has: a taller clipboard lands once, a single row fills the selection.
        const dataRowIndexes: number[] = []
        if (columnSelection) {
          for (let i = 0; i < spreadsheetRows.length; i++) {
            if (!isNonDataRow(spreadsheetRows, i)) dataRowIndexes.push(i)
          }
          if (dataRowIndexes.length === 0) return
        }

        const startRowIndex = columnSelection ? dataRowIndexes[0] : selectedCell.rowNumber
        const singleRowFillEnd = columnSelection
          ? dataRowIndexes[dataRowIndexes.length - 1]
          : selectedCellRange.endRowIndex !== -1
            ? selectedCellRange.endRowIndex
            : selectedCell.rowNumber
        const endRowIndex = rows.length === 1 ? singleRowFillEnd : rows.length + startRowIndex - 1
        const resetStartRowIndex = Math.min(startRowIndex, endRowIndex)
        const resetEndRowIndex = Math.max(startRowIndex, endRowIndex)

        // Find the start column index by ID to avoid virtualization mismatch
        const anchorColumnId = columnSelection ? columnSelection[0].column.publicId : selectedCell.columnId
        const startColIdxInAll = nonGroupedColumns.findIndex((c) => c.column.publicId === anchorColumnId)
        if (!columnSelection && startColIdxInAll === -1) {
          return // Can't find the selected column
        }

        // Track end column index and ID for the final setSelectedCellRange call
        let finalEndColumnIndex = startColIdxInAll
        let finalEndColumnId = anchorColumnId

        // Walk destination rows, consuming paste rows only when the destination is a data row.
        // Non-data rows (add-row placeholder, group header) are passed over without losing a
        // paste row — so a paste that runs past the end of one group lands on the first data
        // row of the next group. For single-row paste, fill every data row within the selection
        // range (repeating rows[0]); for multi-row paste, stop when paste rows are exhausted.
        const isMultiRowPaste = rows.length > 1
        let destIdx = resetStartRowIndex
        let pasteIdx = 0
        let finalEndRowIndex = resetStartRowIndex

        while (destIdx < spreadsheetRows.length) {
          if (isMultiRowPaste && pasteIdx >= rows.length) break
          if (!isMultiRowPaste && destIdx > resetEndRowIndex) break

          const row = spreadsheetRows[destIdx]
          if (!row || isNonDataRow(spreadsheetRows, destIdx)) {
            destIdx++
            continue
          }

          const pasteRow = isMultiRowPaste ? rows[pasteIdx] : rows[0]
          const rowId = 'publicId' in row ? row.publicId : null
          if (rowId) {
            const pasteColumns = pasteRow.split('\t')

            // If the source app left CR on the last cell (common on Windows),
            // remove just that trailing CR so we don't alter legitimate content.
            const lastIdx = pasteColumns.length - 1
            if (lastIdx >= 0) {
              pasteColumns[lastIdx] = pasteColumns[lastIdx].replace(/\n$/, '') // safety if a second trailing LF slipped in
              pasteColumns[lastIdx] = pasteColumns[lastIdx].replace(/\r$/, '')
            }

            // Where this paste row lands: the selected columns, or the rectangle's slice.
            let targetColumns: IViewPortColumn[]
            if (columnSelection) {
              targetColumns = columnSelection
            } else {
              let endColIdxInAll: number
              if (pasteColumns.length === 1 && selectedCellRange.endColumnId) {
                // Single column paste with range selection - use the range end
                endColIdxInAll = nonGroupedColumns.findIndex((c) => c.column.publicId === selectedCellRange.endColumnId)
                if (endColIdxInAll === -1) endColIdxInAll = startColIdxInAll
              } else if (pasteColumns.length === 1) {
                // Single column paste without range - just paste to selected cell
                endColIdxInAll = startColIdxInAll
              } else {
                // Multi-column paste - extend from start by number of paste columns
                endColIdxInAll = Math.min(startColIdxInAll + pasteColumns.length - 1, nonGroupedColumns.length - 1)
              }

              const resetStartColumnIndex = Math.min(startColIdxInAll, endColIdxInAll)
              const resetEndColumnIndex = Math.max(startColIdxInAll, endColIdxInAll)
              targetColumns = nonGroupedColumns.slice(resetStartColumnIndex, resetEndColumnIndex + 1)
            }

            const lastTargetColumnId = targetColumns[targetColumns.length - 1]?.column.publicId
            const lastTargetColumnIndex = nonGroupedColumns.findIndex((c) => c.column.publicId === lastTargetColumnId)
            if (lastTargetColumnIndex > finalEndColumnIndex) {
              finalEndColumnIndex = lastTargetColumnIndex
              finalEndColumnId = lastTargetColumnId || finalEndColumnId
            }

            for (let j = 0; j < targetColumns.length; j++) {
              const valueIndex = pasteColumns.length ? j % pasteColumns.length : 0
              let pasteColumnValue: string | null = pasteColumns[valueIndex] ?? ''

              if (pasteColumnValue.length > 1 && pasteColumnValue.startsWith('"') && pasteColumnValue.endsWith('"')) {
                pasteColumnValue = pasteColumnValue.slice(1, -1)
                pasteColumnValue = pasteColumnValue.replace(/""/g, '"')
              }

              pasteColumnValue = pasteColumnValue === '' ? null : pasteColumnValue
              const column = targetColumns[j]

              if (column) {
                const columnId = column.column.publicId
                cells.push({
                  rowId,
                  columnId,
                  value: pasteColumnValue
                })
              }
            }

            finalEndRowIndex = destIdx
          }

          destIdx++
          if (isMultiRowPaste) pasteIdx++
        }

        const cellsToSend = []
        const newCells = []
        const tempRowDict: Record<string, any> = {}
        for (let i = 0; i < cells.length; i++) {
          const cell = cells[i]
          const _spreadsheetRows = spreadsheetRows as
            | Array<ITableRow | ITableRowInGroup | IGroupRowCreate | IGroupCollapsed>
            | ITableRow[]
            | (ITableRowInGroup | IGroupRowCreate | IGroupCollapsed)[]
          const row = _spreadsheetRows.find(
            (row: ITableRow | (ITableRowInGroup | IGroupRowCreate | IGroupCollapsed)) =>
              'publicId' in row && row.publicId === cell.rowId
          )
          const column =
            allColumns.find((column) => column.column.publicId === cell.columnId) ??
            (treeLeafColumn?.column.publicId === cell.columnId ? treeLeafColumn : undefined)

          if (column && row && 'publicId' in row) {
            const oldValue = getRowValue(
              row as ITableRow,
              column.column.publicId,
              spreadsheetData.columnIdToIndex,
              spreadsheetData.columnStore
            )

            // Keep an updated version of row data with values from the paste
            // so other cells can be validated against the new values
            // Convert array to object for validation functions that expect object format
            tempRowDict[cell.rowId] = {
              ...(cell.rowId in tempRowDict
                ? tempRowDict[cell.rowId]
                : rowDataArrayToObject(
                    row.rowData,
                    spreadsheetData.indexToColumnId,
                    row._rowIndex,
                    spreadsheetData.columnStore
                  )),
              [cell.columnId]: cell.value
            }

            try {
              if (
                isReadOnly(column, spreadsheetData.isContributor, spreadsheetData.userConfiguration.rowLockSettings)
              ) {
                // break if the column is read only without a throw
                setSnackbarMessage({
                  status: 'warning',
                  message: `Column '${column.column.name}' is read-only, so nothing was pasted into it.`
                })
              } else {
                let dateFormat
                let datetimeFormat
                if (column.column.dateFormat) {
                  if (column.column.kind == 'datetime') {
                    datetimeFormat = column.column.dateFormat as AllowedDatetimeFormats
                  } else {
                    dateFormat = column.column.dateFormat as AllowedDateFormats
                  }
                }

                cell.value = transformStringToCorrectFormat(
                  cell.value as string | null,
                  column.column.kind,
                  dateFormat,
                  datetimeFormat
                )

                const validValue = isValidValue(
                  cell.value,
                  tempRowDict[cell.rowId],
                  spreadsheetData.columnValuesCount,
                  column.column,
                  undefined,
                  spreadsheetData.viewDetails.type
                )
                const validatedValue = validatePasteField(cell.value, column.column)

                if ((validValue.error && !validValue.warn) || !validatedValue) {
                  // Built-in messages name the value and column already; a column's own validation
                  // message may not, so give it that context.
                  throw column.column.validationMessage
                    ? `'${cell.value}' can't be pasted into column '${column.column.name}'. ${validValue.errorMessage}`
                    : validValue.errorMessage
                }

                const sameValue = checkValuesSame(cell.value, oldValue, column.column.kind)
                if (!sameValue) cellsToSend.push(i)
              }
            } catch (error) {
              const message: string = error ? (error as string) : ''
              setSnackbarMessage({ status: 'error', message })
            }
          }
        }

        for (let i = 0; i < cellsToSend.length; i++) {
          newCells.push(cells[cellsToSend[i]])
        }

        let mappedNewCells = newCells
        const mappedPayload = viewAdapter.mapCellsForWrite({
          cells: newCells,
          context: { projectId: project.publicId }
        })
        if (!mappedPayload) return
        mappedNewCells = mappedPayload.cells
        viewAdapter.transformPastedCells(mappedNewCells, spreadsheetData.viewDetails.columns)

        // Update the cells locally
        setSpreadsheetData({
          type: 'BULK_EDIT_CELL',
          cells: mappedNewCells.map((cell) => {
            return { rowId: cell.rowId, columnId: cell.columnId, value: { [cell.columnId]: cell.value } }
          })
        })

        // Send cells to the backend (non-blocking — changes are already applied above)
        if (mappedNewCells.length > 0) {
          const cellUpdates = {
            cells: mappedNewCells,
            context: { projectId: project.publicId }
          }

          updateTableViewCellsAction(
            spreadsheetData.viewDetails.publicId,
            cellUpdates,
            spreadsheetData,
            setSpreadsheetData,
            () => {
              return null
            },
            (error) => {
              displayErrorMessage(error)
              return null
            }
          )
        }

        // A column selection has no anchor cell, so a range here would highlight nothing. Keep it.
        if (columnSelection) return

        // The paste loop already lands finalEndRowIndex on the last data row it wrote to,
        // which naturally skips over add-row / group-header rows encountered along the way.
        setSelectedCellRange({
          endRowIndex: finalEndRowIndex,
          endColumnIndex: finalEndColumnIndex,
          endColumnId: finalEndColumnId
        })
      }

      // The freeze on a large table is the *synchronous* paste work above (validation +
      // the optimistic re-render). Paint the overlay and let it render BEFORE that work,
      // then clear it as soon as the synchronous work is done — the clear batches with the
      // optimistic update, so the overlay covers exactly the frozen frame and disappears
      // when the data lands. The network persist continues in the background, unblocked, so
      // there's no need to keep the overlay up for it. Smaller tables don't freeze, so they
      // skip the overlay entirely (no flash).
      if (spreadsheetData.totalRows >= LARGE_TABLE_ROW_THRESHOLD) {
        setSpreadsheetData({ type: 'REFRESH', refreshing: true, label: 'Updating cells…' })
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            try {
              runPaste()
            } finally {
              setSpreadsheetData({ type: 'REFRESH', refreshing: false })
            }
          })
        )
      } else {
        runPaste()
      }
    },
    [
      // spreadsheet state
      rawRows,
      groupSettings,
      isContributor,
      columnValuesCount,

      // viewport + selection
      columns,
      allColumns,
      allVisibleColumnIds,
      expandedRowsWithGroups,
      selectedCell,
      selectedCellRange,
      selectedColumnIds,

      // view / project context
      viewType,
      viewId,
      project.publicId,

      // stable dispatchers you can omit if you like
      setSnackbarMessage,
      setSpreadsheetData,
      displayErrorMessage
    ]
  )

  const handlePaste = (event: ClipboardEvent) => {
    if (isFocusedRef.current && !handleEvent(event)) {
      event.preventDefault()
      event.clipboardData
        ? handlePasteText(event.clipboardData.getData('text/plain'))
        : displayErrorMessage(CLIPBOARD_UNAVAILABLE_MESSAGE)
    }
  }

  const handleKeyDownListener = (event: KeyboardEvent) => {
    // Escape clears a whole-column selection even though the grid cell isn't focused
    // (column selection deliberately does not set isFocused).
    if (event.key === 'Escape' && selectedColumnIds.length > 0) {
      setSelectedColumnIds([])
      setColumnSelectionAnchorId(null)
    }

    // Same for a whole-row selection — a modifier-click selection deliberately leaves the grid
    // unfocused, so it can't rely on the isFocused-gated handling below.
    if (event.key === 'Escape' && selectedRowIds.length > 0) {
      setSelectedRowIds([])
      setRowSelectionAnchorId(null)
    }

    if (event.key === 'Escape' && selectedGroupCell) setSelectedGroupCell(null)

    if (isFocusedRef.current && !handleEvent(event)) {
      const rows = renderedRows
      const isUndoEvent = (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'z'
      const isRedoEvent =
        (event.metaKey || event.ctrlKey) &&
        ((event.shiftKey && event.key.toLowerCase() === 'z') || event.key.toLowerCase() === 'y')

      // Ctrl/Cmd+A selects every cell in the view.
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'a' &&
        !selectedCell.editing
      ) {
        event.preventDefault()
        selectAllCells()
        return
      }

      // Ctrl+Enter or Cmd+Enter to create new row when in last row
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === 'Enter' &&
        !event.shiftKey &&
        !spreadsheetData.viewDetails.disableNewRow
      ) {
        if (spreadsheetData.userConfiguration.groupSettings.length > 0) {
          // The add-row itself is the button.
          const addGroupRowElement = document.getElementById(`add-group-row-${uniqueNumber}-${selectedCell.rowId}`)

          if (addGroupRowElement) {
            event.preventDefault()
            addGroupRowElement.click()
            return
          }
        }

        // Otherwise, check if we're on the last row overall
        const rowNumber = rows.findIndex(
          (row) => 'publicId' in row && (row as ITableRow).publicId === selectedCell.rowId
        )

        if (rowNumber === rows.length - 1) {
          const addRowElement = document.getElementById(`add-row-${uniqueNumber}`)

          if (addRowElement) {
            event.preventDefault()
            addRowElement.click()
          }
        }
      }

      // Delete or Backspace key event (Backspace is the primary delete key on Mac)
      if (event.key === KEY_PRESS.delete || event.key === KEY_PRESS.backspace) {
        // Suppress any browser default (e.g. Backspace history navigation) since
        // the grid — not an input — is focused here.
        event.preventDefault()
        clearSelectedRange()
      }

      // Undo or redo event
      if (isUndoEvent || isRedoEvent) {
        const historyPoint = isUndoEvent ? spreadsheetData.historyPoint : spreadsheetData.historyPoint + 1
        const newHistoryPoint = isUndoEvent ? historyPoint - 1 : historyPoint

        if (spreadsheetData.history[historyPoint]) {
          const event = spreadsheetData.history[historyPoint]

          // Handle EDIT_CELL event
          if (event && event.type === 'EDIT_CELL' && event.cells) {
            // Update the cells locally
            setSpreadsheetData({
              type: 'BULK_EDIT_CELL',
              cells: event.cells.map((cell: ITableViewHistoryCell) => {
                return {
                  rowId: cell.rowId,
                  columnId: cell.columnId,
                  value: { [cell.columnId]: isUndoEvent ? cell.oldValue : cell.newValue }
                }
              })
            })

            // Restore selection snapshot if this entry carries one (drag-fill does)
            if (isUndoEvent) {
              if (event.prevSelectedCell) {
                setSelectedCell(event.prevSelectedCell)
              }
              if (event.prevSelectedCellRange) {
                updateSelectedCellRange(event.prevSelectedCellRange)
              }
            } else {
              if (event.nextSelectedCell) {
                setSelectedCell(event.nextSelectedCell)
              }
              if (event.nextSelectedCellRange) {
                updateSelectedCellRange(event.nextSelectedCellRange)
              }
            }

            // Move the cell history point
            setSpreadsheetData({
              type: 'UPDATE_HISTORY_POINT',
              point: newHistoryPoint
            })

            // Update the cells
            updateTableViewCellsAction(
              spreadsheetData.viewDetails.publicId,
              {
                cells: event.cells.map((cell: ITableViewHistoryCell) => {
                  return {
                    rowId: cell.rowId,
                    columnId: cell.columnId,
                    value: isUndoEvent ? cell.oldValue : cell.newValue
                  }
                }),
                context: event.context
              },
              spreadsheetData,
              setSpreadsheetData,
              () => {
                return null
              },
              (error) => {
                displayErrorMessage(error)
                return null
              },
              true
            )
          }
        }
      }
    }
  }

  // Use refs to hold the latest handlers - this prevents re-registering event listeners
  // every time state changes, which was causing memory leaks from stale closures
  const handleCopyRef = useRef(handleCopy)
  const handleCutRef = useRef(handleCut)
  const handlePasteRef = useRef(handlePaste)
  const handleKeyDownListenerRef = useRef(handleKeyDownListener)

  // Update refs when handlers change (every render)
  useEffect(() => {
    handleCopyRef.current = handleCopy
    handleCutRef.current = handleCut
    handlePasteRef.current = handlePaste
    handleKeyDownListenerRef.current = handleKeyDownListener
  })

  // Register event listeners once - they call through refs to get latest handlers
  useEffect(() => {
    const onCopy = (e: ClipboardEvent) => handleCopyRef.current(e)
    const onCut = (e: ClipboardEvent) => handleCutRef.current(e)
    const onPaste = (e: ClipboardEvent) => handlePasteRef.current(e)
    const onKeyDown = (e: KeyboardEvent) => handleKeyDownListenerRef.current(e)

    document.addEventListener('copy', onCopy)
    document.addEventListener('cut', onCut)
    document.addEventListener('paste', onPaste)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('cut', onCut)
      document.removeEventListener('paste', onPaste)
      document.removeEventListener('keydown', onKeyDown)
      // Clear handler refs to release closures that hold row data
      handleCopyRef.current = () => {}
      handleCutRef.current = () => {}
      handlePasteRef.current = () => {}
      handleKeyDownListenerRef.current = () => {}
    }
  }, []) // Empty dependency array - listeners registered once

  // Listen for any change in the user selecting a searched cell
  useEffect(() => {
    const searchedCell = spreadsheetData.selectedSearchCell
    if (searchedCell.rowId && searchedCell.columnId) {
      setSelectedCell({
        rowId: searchedCell.rowId,
        columnId: searchedCell.columnId,
        rowNumber: searchedCell.rowNumber,
        columnNumber: searchedCell.columnNumber,
        editing: false,
        value: ''
      })
    }
  }, [spreadsheetData.selectedSearchCell])

  // Combined filtering and sorting effect
  // Filters rows first (if needed), then sorts the result in a single state update.
  // Uses a visibility bitmap instead of splitting rows into two arrays.
  // Waits for rowStreamComplete before running — this keeps the loading indicator visible
  // through both the row streaming and the filter/sort application phases
  useEffect(() => {
    if (!spreadsheetData.rowStreamComplete) {
      return
    }

    const slicerFilters = buildSlicerRuntimeFilters(spreadsheetData.viewDetails.columns, slicerSelections)
    const effectiveFilters = mergeSlicerFilters(spreadsheetData.userConfiguration.filterSettings, slicerFilters)

    const applyFiltersAndSort = () => {
      // Check if filtering is needed - skip filterRows entirely when no filters are active
      const hasFilters = effectiveFilters.length > 0
      const hasValidationFilter = spreadsheetData.viewDetails.displayValidationErrorRows !== 0
      const hasCommentFilter = spreadsheetData.viewDetails.displayCommentRows !== 0
      const needsFiltering = hasFilters || hasValidationFilter || hasCommentFilter || filterSearchRows

      // Rows that are already in order with nothing to filter need no new rows array: skipping the
      // copy and the dispatch spares a full re-render on large tables (the progressive stream has
      // just finished rendering exactly these rows).
      const alreadyInOrder =
        spreadsheetData.userConfiguration.sortSettings.length === 0 && isSortedBySortOrder(spreadsheetData.rows)
      if (!needsFiltering && spreadsheetData.visibleBitmap === null && alreadyInOrder) {
        if (spreadsheetData.loading) {
          setSpreadsheetData({ type: 'LOADING', loading: false })
        }
        return
      }

      // Sort first, then compute bitmap — bitmap indices must match sorted order. Rows that are
      // already in their unsorted order are kept as they are: a fresh copy of a million rows would
      // only make every consumer of the rows array recompute for a filter-only change.
      const sortedRows = alreadyInOrder
        ? spreadsheetData.rows
        : sortRows(
            spreadsheetData.rows,
            spreadsheetData.userConfiguration.sortSettings,
            spreadsheetData.viewDetails.columns,
            spreadsheetData.columnIdToIndex,
            spreadsheetData.columnStore
          )

      let bitmap: Uint8Array | null = null
      let bitmapVisibleCount: number | undefined
      if (needsFiltering) {
        const filterResult = filterRows(
          sortedRows,
          spreadsheetData.columnValuesCount,
          effectiveFilters,
          user ? user.email : '',
          user ? user.publicId : '',
          tags ? tags.filter((tag) => tag.projectPublicId === project.publicId) : [],
          spreadsheetData.processTags,
          spreadsheetData.processVariables,
          spreadsheetData.processVariableValues,
          spreadsheetData.viewDetails.columns,
          spreadsheetData.viewDetails.displayValidationErrorRows,
          spreadsheetData.viewDetails.displayCommentRows,
          spreadsheetData.columnIdToIndex,
          spreadsheetData.searchedRows,
          spreadsheetData.comments,
          filterSearchRows,
          spreadsheetData.columnStore
        )
        bitmap = filterResult.bitmap
        bitmapVisibleCount = filterResult.visibleCount
      }
      // null bitmap = all visible (no filtering needed)

      setSpreadsheetData({
        type: 'SET_SPREADSHEET_ROWS',
        rows: sortedRows,
        totalRows: sortedRows.length,
        visibleBitmap: bitmap,
        visibleCount: bitmapVisibleCount
      })
      if (spreadsheetData.loading) {
        setSpreadsheetData({ type: 'LOADING', loading: false })
      }
    }

    // When transitioning from row streaming to filter/sort application, defer the heavy
    // computation so the browser can paint the "Applying filters..." indicator before the
    // main thread is blocked by filterRows/sortRows on large datasets.
    // useEffect already runs after the browser paints, and setTimeout defers to the next
    // macrotask, giving the browser a frame to render the spinner.
    if (spreadsheetData.loading) {
      const timeoutId = setTimeout(applyFiltersAndSort, 0)
      return () => clearTimeout(timeoutId)
    }

    applyFiltersAndSort()
  }, [
    spreadsheetData.viewDetails.displayValidationErrorRows,
    spreadsheetData.viewDetails.displayCommentRows,
    spreadsheetData.userConfiguration.filterSettings,
    spreadsheetData.userConfiguration.sortSettings,
    Object.keys(spreadsheetData.columnValuesCount).length,
    spreadsheetData.rowStreamComplete,
    spreadsheetData.searchTerm,
    filterSearchRows,
    user,
    slicerSelections
  ])

  // "Default Collapse Groups on Load" — write the view's default into `collapsedGroupIds` once the
  // grouped rows actually exist. Waiting on `loading` alone isn't enough: `groupedRows` is built
  // from a deferred copy of the rows, so on the render where loading flips to false it is still
  // the previous (empty) grouping, and collapsing then walks zero group keys and does nothing.
  // Keying the one-shot on the view id means switching view re-applies the default, while a group
  // the user expands afterwards is never collapsed again behind their back.
  //
  // `collapseAllGroupsPending` (computed above) has already rendered those groups collapsed, so
  // this only makes the state real — as a layout effect, before paint, so the two never disagree
  // on screen.
  useLayoutEffect(() => {
    const viewId = spreadsheetData.viewDetails.publicId
    if (spreadsheetData.loading || defaultCollapseAppliedForViewRef.current === viewId) return
    // Grouping is on (`groupedRows` is defined) but the grouped rows haven't caught up yet.
    if (groupedRows && Object.keys(groupedRows).length === 0) return

    defaultCollapseAppliedForViewRef.current = viewId
    if (!groupedRows || !spreadsheetData.viewDetails.collapsedGroupView) return

    try {
      handleToggleCollapseAll()
    } catch {
      /* no-op  */
    }
  }, [
    spreadsheetData.loading,
    spreadsheetData.viewDetails.publicId,
    spreadsheetData.viewDetails.collapsedGroupView,
    groupedRows
  ])

  const clearRowSelection = (): void => {
    setSelectedRowIds([])
    setRowSelectionAnchorId(null)
  }

  const selectGroupCell = (groupCell: ISelectedGroupCell): void => {
    setSelectedCell(INITIAL_SELECTED_CELL)
    clearRowSelection()
    setSelectedColumnIds([])
    setColumnSelectionAnchorId(null)
    setSelectedGroupCell(groupCell)
    setIsFocused(true)
  }

  const copyGroupCellValue = (groupCell: ISelectedGroupCell): void => {
    selectGroupCell(groupCell)

    if (!navigator.clipboard?.writeText) {
      displayErrorMessage(CLIPBOARD_UNAVAILABLE_MESSAGE)
      return
    }

    void navigator.clipboard
      .writeText(createCopyFromSelectedCell(groupCell))
      .then(() => setSnackbarMessage({ status: 'success', message: 'Copied to clipboard' }))
      .catch(displayErrorMessage)
  }

  // The displayed data rows, in display order — group headers, add-rows and rows hidden by a
  // filter are all excluded, so a row range only ever spans what the user can actually see.
  const getDisplayedDataRowIds = (): string[] => {
    const orderedRowIds: string[] = []
    for (let i = 0; i < renderedRows.length; i++) {
      const row = renderedRows[i]
      if (isDataRow(row)) orderedRowIds.push(row.publicId)
    }
    return orderedRowIds
  }

  // Selecting whole rows also selects their cells, so Delete / Copy / Cut act on the selection
  // exactly as they do on a dragged range. The cell model is a single rectangle (anchor cell +
  // end of range), so the selected rows are mirrored into it spanning every selectable column.
  //
  // A gappy Ctrl/Cmd+click selection has no rectangle that covers it without also covering the
  // rows in the gaps — and Delete over those would wipe data the user never selected — so the
  // cell selection is dropped instead. ``clearSelectedRange`` reads the row selection directly,
  // which keeps Delete working on gappy selections without any rectangle at all.
  const selectCellsForRows = (rowIdsToSelect: string[], touchedRowId: string): void => {
    const columns = getSelectableColumns()
    const orderedRowIds = getDisplayedDataRowIds()
    // Grouping puts an add-row between data rows, so a row's place among the data rows (used below
    // to check the selection has no gaps) differs from its rendered index (what rowNumber means).
    const renderedIndexByRowId = new Map<string, number>()
    for (let i = 0; i < renderedRows.length; i++) {
      const row = renderedRows[i]
      if (isDataRow(row)) renderedIndexByRowId.set(row.publicId, i)
    }

    const dataPositions = rowIdsToSelect.map((id) => orderedRowIds.indexOf(id)).filter((index) => index !== -1)

    // Selecting rows focuses the grid, which is what gates the document-level Delete / Copy / Cut
    // handling. Ordering matters in both directions: ``setSelectedCellRange`` silently discards a
    // real range while the grid is unfocused, so focus is asserted before the selection is
    // written; but clearing the anchor cell drops focus as a side effect, so a clear re-asserts it.
    const focusGrid = (): void => {
      if (rowIdsToSelect.length > 0) setIsFocused(true)
    }

    const dropCellSelection = (): void => {
      setSelectedCell(INITIAL_SELECTED_CELL)
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      focusGrid()
    }

    if (dataPositions.length === 0 || columns.length === 0) return dropCellSelection()

    const firstDataPosition = Math.min(...dataPositions)
    const lastDataPosition = Math.max(...dataPositions)
    if (lastDataPosition - firstDataPosition + 1 !== dataPositions.length) return dropCellSelection()

    // Anchor on whichever end of the block the user just touched, with the range running to the
    // other end. Scroll-into-view doesn't try to scroll the user to the other end then
    const firstRowId = orderedRowIds[firstDataPosition]
    const lastRowId = orderedRowIds[lastDataPosition]
    const [anchorRowId, endRowId] = touchedRowId === lastRowId ? [lastRowId, firstRowId] : [firstRowId, lastRowId]

    const anchorRowNumber = renderedIndexByRowId.get(anchorRowId)
    const endRowIndex = renderedIndexByRowId.get(endRowId)
    if (anchorRowNumber === undefined || endRowIndex === undefined) return dropCellSelection()

    focusGrid()
    setSelectedCell({
      rowId: anchorRowId,
      columnId: columns[0].column.publicId,
      rowNumber: anchorRowNumber,
      columnNumber: 0,
      value: '',
      editing: false
    })
    setSelectedCellRange({
      endRowIndex,
      endColumnIndex: columns.length - 1,
      endColumnId: columns[columns.length - 1].column.publicId
    })
  }

  // Ctrl/Cmd+A — select every cell in the current view.
  //
  // The grid only renders the rows and columns near the viewport, so the rectangle is built from
  // the full logical lists rather than from what happens to be on screen. Every selection-wide
  // operation (copy in particular) reads the underlying data through ``getSelectionTarget``, so
  // the off-screen cells come along with it.
  const selectAllCells = (): void => {
    const selectAll = buildSelectAllSelection(renderedRows, getSelectableColumns())
    if (!selectAll) return

    // A whole-row selection is authoritative in ``getSelectionTarget``, so it has to be dropped
    // first — otherwise select-all would still only cover the rows that were already selected.
    clearRowSelection()
    setSelectedCell(selectAll.selectedCell)
    setSelectedCellRange(selectAll.selectedCellRange)
  }

  // The data rows the selection covers, in display order. Selecting whole rows also selects their
  // cells, so both entry points — the row-number right-click and an ordinary drag / Shift+Arrow
  // cell range — describe the same thing. This is what "N rows selected" counts.
  //
  // Indexes ``renderedRows`` rather than the row-id list, because the selection's row numbers are
  // indexes into the rendered list, and skips the group headers / add-rows sitting inside it.
  const getSelectedRowIds = (): string[] => {
    // A column selection reaches every loaded row, but that is its scope, not "these rows are
    // selected" — feeding it to Delete Rows would offer to delete the table.
    if (selectedRowIds.length === 0 && selectedColumnIds.length > 0) return []

    const { rowIndexes } = getSelectionTarget()

    const rowIds: string[] = []
    for (const i of rowIndexes) {
      const row = renderedRows[i]
      if (row && isDataRow(row)) rowIds.push(row.publicId)
    }
    return rowIds
  }

  // Of those, the ones a delete may actually remove. A row locked by a row-lock rule with
  // ``applyToEntireRow`` is dropped rather than failing the run: the rule exists to protect that
  // row, so deleting it would go straight around the lock.
  const getRowIdsToDelete = (): string[] => {
    const rowLockSettings = spreadsheetData.userConfiguration.rowLockSettings
    if (rowLockSettings.length === 0) return getSelectedRowIds()

    const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((column) => [column.publicId, column]))
    const rowsById = new Map(spreadsheetData.rows.map((row) => [row.publicId, row]))

    return getSelectedRowIds().filter((rowId) => {
      const row = rowsById.get(rowId)
      if (!row) return false
      return !getRowLockState(
        rowLockSettings,
        row,
        columnLookup,
        spreadsheetData.columnIdToIndex,
        spreadsheetData.columnValuesCount,
        spreadsheetData.columnStore
      ).rowLocked
    })
  }

  // Whole-row selection driven from the row-number cell: single (plain click or SHIFT+SPACE),
  // contiguous range (Shift+click from the anchor), or toggled individual rows (Ctrl/Cmd+click).
  const handleRowNumberSelect = (rowId: string, options: { shiftKey: boolean; toggleKey: boolean }): void => {
    const next = computeRowSelection(
      getDisplayedDataRowIds(),
      { selectedRowIds, anchorId: rowSelectionAnchorId },
      rowId,
      options
    )
    setSelectedRowIds(next.selectedRowIds)
    setRowSelectionAnchorId(next.anchorId)
    // Same exclusivity the other way. A contiguous selection clears columns anyway via
    // ``setSelectedCell``, but a gappy one writes no anchor cell and so would leave both set.
    clearColumnSelection()
    selectCellsForRows(next.selectedRowIds, rowId)
  }

  // Shift+ArrowUp / Shift+ArrowDown while a whole-row selection is active grows or shrinks it by
  // one displayed row. Returns false when there is no row selection to extend
  const extendRowSelectionByKeyboard = (direction: 1 | -1): boolean => {
    if (selectedRowIds.length === 0) return false

    const orderedRowIds = getDisplayedDataRowIds()
    const next = extendRowSelection(orderedRowIds, { selectedRowIds, anchorId: rowSelectionAnchorId }, direction)
    setSelectedRowIds(next.selectedRowIds)
    setRowSelectionAnchorId(next.anchorId)
    // Anchor the active cell on the moving end of the range, so the grid stays at the edge the
    // user is working on. That end is the range's focus row, which shrinking moves too — reading
    // it back is why this can't just look for a newly added row.
    selectCellsForRows(next.selectedRowIds, getRowSelectionFocusId(orderedRowIds, next) ?? '')
    return true
  }

  // Editing a cell is working in that one cell, so it leaves whole-row selection mode too.
  useEffect(() => {
    if (!selectedCell.editing) return
    setSelectedRowIds((prev) => (prev.length ? [] : prev))
    setRowSelectionAnchorId((prev) => (prev === null ? prev : null))
  }, [selectedCell.editing])

  const handleRowNumberSelectRef = useRef(handleRowNumberSelect)
  const extendRowSelectionByKeyboardRef = useRef(extendRowSelectionByKeyboard)

  useEffect(() => {
    handleRowNumberSelectRef.current = handleRowNumberSelect
    extendRowSelectionByKeyboardRef.current = extendRowSelectionByKeyboard
  })

  const handleKeyDown = useCallback((e: React.KeyboardEvent, newSelectedCell: ISelectedCell) => {
    // SHIFT+SPACE selects the active cell's row (Excel). Lives here, not on a document listener,
    // because the cell's own space branch stops propagation.
    if (e.key === ' ' && e.shiftKey && !e.ctrlKey && !e.metaKey && newSelectedCell.currentRowId !== '') {
      e.preventDefault()
      handleRowNumberSelectRef.current(newSelectedCell.currentRowId, { shiftKey: false, toggleKey: false })
      return
    }

    // Holding Shift and arrowing up/down extends a whole-row selection by whole rows rather than growing a cell range
    if ((e.key === KEY_PRESS.up || e.key === KEY_PRESS.down) && e.shiftKey && !e.ctrlKey && !e.metaKey) {
      if (extendRowSelectionByKeyboardRef.current(e.key === KEY_PRESS.down ? 1 : -1)) {
        e.preventDefault()
        return
      }
    }

    // Any other keyboard navigation moves the active cell, which drops out of whole-row selection
    // and back to an ordinary single-cell selection.
    if (KEY_PRESSES.includes(e.key) || e.key === KEY_PRESS.pageUp || e.key === KEY_PRESS.pageDown) {
      setSelectedRowIds((prev) => (prev.length ? [] : prev))
      setRowSelectionAnchorId((prev) => (prev === null ? prev : null))
    }

    // Do not reset selected cell range if copying, pasting or cutting
    if (
      e.key !== KEY_PRESS.delete &&
      e.key !== KEY_PRESS.backspace &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.shiftKey &&
      e.key !== 'Meta' &&
      e.key !== KEY_PRESS.pageUp &&
      e.key !== KEY_PRESS.pageDown
    ) {
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    }

    // Disable tab key press so that we don't jump from cell to cell through tab index
    if (e.key === 'Tab') {
      e.preventDefault()
    } else if (e.key === KEY_PRESS.pageUp || e.key === KEY_PRESS.pageDown) {
      e.preventDefault()

      // Prevent overlapping navigations
      if (isPageNavigatingRef.current) {
        return
      }

      isPageNavigatingRef.current = true

      // Store the starting cell for establishing the anchor (if this is the first shift press)
      const anchorCell = selectedCellRef.current
      const currentSelectedCell = selectedCellRef.current
      const allRows = allRowsRef.current

      // Calculate how many rows fit in the viewport
      const viewportHeight = gridHeightRef.current - stickyContentHeightRef.current
      const pageSize = Math.max(1, Math.floor(viewportHeight / rowHeightRef.current))

      // Calculate target based on current position
      let targetRowIndex: number
      if (e.key === KEY_PRESS.pageUp) {
        targetRowIndex = Math.max(0, currentSelectedCell.rowNumber - pageSize)
      } else {
        targetRowIndex = Math.min(allRows.length - 1, currentSelectedCell.rowNumber + pageSize)
      }

      const targetRow = allRows[targetRowIndex]

      if (!targetRow || !('publicId' in targetRow)) {
        isPageNavigatingRef.current = false
        return
      }

      if (e.shiftKey) {
        const currentRange = selectedCellRangeRef.current
        if (currentRange.endRowIndex === -1) {
          setSelectedCellRange({
            endRowIndex: anchorCell.rowNumber,
            endColumnIndex: anchorCell.columnNumber,
            endColumnId: anchorCell.columnId
          })
        }
      } else {
        setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      }

      updateSelectedCell({
        rowId: targetRow.publicId,
        columnId: currentSelectedCell.columnId,
        editing: false,
        value: '',
        rowNumber: targetRowIndex,
        columnNumber: currentSelectedCell.columnNumber
      })

      scrollToLogical(targetRowIndex * rowHeightRef.current)

      // Allow next navigation after a brief delay to ensure DOM updates complete
      requestAnimationFrame(() => {
        isPageNavigatingRef.current = false
      })
    } else if (KEY_PRESSES.includes(e.key) && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()

      if (!e.shiftKey) {
        setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
      }

      updateSelectedCell((prevSelected) => {
        if (!gridRef.current) {
          return { ...prevSelected }
        }

        let newRowId = prevSelected.rowId
        let newColumnId = prevSelected.columnId
        let newRowNumber = prevSelected.rowNumber
        let newColumnNumber = prevSelected.columnNumber

        switch (e.key) {
          case KEY_PRESS.left:
            gridRef.current.scrollLeft = 0
            newColumnId = newSelectedCell.startColumnId
            newColumnNumber = 0
            break
          case KEY_PRESS.right:
            gridRef.current.scrollLeft = gridRef.current.scrollWidth
            newColumnId = newSelectedCell.finalColumnId
            newColumnNumber = newSelectedCell.finalColumnNumber
            break
          case KEY_PRESS.down: {
            scrollToLogical(maxScrollTopRef.current)
            if (isGroupedRef.current && expandedRowsRef.current.length > 0) {
              for (let idx = expandedRowsRef.current.length - 1; idx >= 0; idx--) {
                if ('publicId' in expandedRowsRef.current[idx]) {
                  newRowId = (expandedRowsRef.current[idx] as ITableRow).publicId
                  newRowNumber = idx
                  break
                }
              }
            } else {
              newRowId = newSelectedCell.finalRowId
              newRowNumber = newSelectedCell.finalRowNumber
            }
            break
          }
          case KEY_PRESS.up: {
            scrollToLogical(0)
            if (isGroupedRef.current && expandedRowsRef.current.length > 0) {
              for (let idx = 0; idx < expandedRowsRef.current.length; idx++) {
                if ('publicId' in expandedRowsRef.current[idx]) {
                  newRowId = (expandedRowsRef.current[idx] as ITableRow).publicId
                  newRowNumber = idx
                  break
                }
              }
            } else {
              newRowId = newSelectedCell.startRowId
              newRowNumber = 0
            }
            break
          }
        }

        return {
          rowId: newRowId,
          columnId: newColumnId,
          editing: false,
          value: '',
          rowNumber: newRowNumber,
          columnNumber: newColumnNumber
        }
      })
    } else if (KEY_PRESSES.includes(e.key)) {
      e.preventDefault()
      updateSelectedCell((prevSelected) => {
        let newRowId = prevSelected.rowId
        let newColumnId = prevSelected.columnId
        let newRowNumber = prevSelected.rowNumber
        let newColumnNumber = prevSelected.columnNumber

        switch (e.key) {
          case KEY_PRESS.left:
            if (newSelectedCell.prevColumnId !== '') {
              newColumnId = newSelectedCell.prevColumnId
              newColumnNumber -= 1
            }
            break
          case KEY_PRESS.right:
            if (newSelectedCell.nextColumnId !== '') {
              newColumnId = newSelectedCell.nextColumnId
              newColumnNumber += 1
            }
            break
          case KEY_PRESS.down:
            if (newSelectedCell.nextRowId !== '') {
              newRowId = newSelectedCell.nextRowId
              newRowNumber += 1
            } else {
              // Immediate neighbor is a non-data row (add-row / group header).
              // Skip past it to the next data row. Shift+arrow does the same so the
              // extended selection crosses group boundaries in step with drag selection.
              const rows = renderedRowsRef.current
              const target = findNearestDataRow(rows, prevSelected.rowNumber + 1, 1)
              if (target !== -1) {
                newRowId = (rows[target] as ITableRow).publicId
                newRowNumber = target
              }
            }
            break
          case KEY_PRESS.up:
            if (newSelectedCell.prevRowId !== '') {
              newRowNumber -= 1
              newRowId = newSelectedCell.prevRowId
            } else {
              const rows = renderedRowsRef.current
              const target = findNearestDataRow(rows, prevSelected.rowNumber - 1, -1)
              if (target !== -1) {
                newRowId = (rows[target] as ITableRow).publicId
                newRowNumber = target
              }
            }
            break
        }

        // No movement — bail out with the same reference to avoid unnecessary re-renders
        if (newRowId === prevSelected.rowId && newColumnId === prevSelected.columnId) {
          return prevSelected
        }

        // Update unconditionally — the target cell may be outside the virtualization
        // buffer and therefore absent from the DOM. The scroll-into-view effect on
        // selectedCell.rowId/columnId will bring it into view on the next render,
        // matching the PageUp/PageDown pattern above.
        return {
          rowId: newRowId,
          columnId: newColumnId,
          editing: false,
          value: '',
          rowNumber: newRowNumber,
          columnNumber: newColumnNumber
        }
      })
    }
  }, [])

  const handleKeyDownForm = useCallback((e: React.KeyboardEvent, newSelectedCell: ISelectedCell) => {
    // Disable tab key press so that we don't jump from cell to cell through tab index
    if (e.key === 'Tab') {
      e.preventDefault()
    } else if (KEY_PRESSES.includes(e.key)) {
      e.preventDefault()
      updateSelectedCell((prevSelected) => {
        const newRowId = prevSelected.rowId
        const newRowNumber = prevSelected.rowNumber
        let newColumnId = prevSelected.columnId
        let newColumnNumber = prevSelected.columnNumber

        switch (e.key) {
          case KEY_PRESS.left:
          case KEY_PRESS.up:
            if (newSelectedCell.prevColumnId !== '') {
              newColumnId = newSelectedCell.prevColumnId
              newColumnNumber += 1
            }
            break
          case KEY_PRESS.right:
          case KEY_PRESS.down:
            if (newSelectedCell.nextColumnId !== '') {
              newColumnNumber -= 1
              newColumnId = newSelectedCell.nextColumnId
            }
            break
        }

        // No movement — bail out with the same reference to avoid unnecessary re-renders
        if (newColumnId === prevSelected.columnId) {
          return prevSelected
        }

        const cellId = createCellId(newRowId, newColumnId, uniqueNumber)
        const newCell = checkSelectedCellExists(cellId)
        if (newCell) {
          return {
            rowId: newRowId,
            columnId: newColumnId,
            editing: false,
            value: '',
            rowNumber: newRowNumber,
            columnNumber: newColumnNumber
          }
        }

        return prevSelected
      })
    }
  }, [])

  const frozenLeftOffset =
    lastFrozenColumnIndex >= 0
      ? allColumns[lastFrozenColumnIndex].left + allColumns[lastFrozenColumnIndex].column.width
      : ROW_NUMBER_COLUMN_WIDTH + treeColumnWidth

  // This useeffect looks for changes in the selected row to see whether to scroll up or down
  useEffect(() => {
    const newRowId = selectedCell.rowId
    if (newRowId !== '' && gridRef.current && gridHeight > 0) {
      let rowNumber
      if (expandedRowsWithGroups && expandedRowsWithGroups.length !== 0) {
        rowNumber = expandedRowsWithGroups.findIndex(
          (row) => 'publicId' in row && (row as ITableRow).publicId === newRowId
        )
      } else {
        rowNumber = (renderedRows as ITableRow[]).findIndex(
          (row: ITableRow | ITableRowInGroup | IGroupRowCreate | IGroupCollapsed) =>
            'publicId' in row && row.publicId === newRowId
        )
      }

      // Only scroll if the row was found (rowNumber >= 0)
      // Skip scrolling for the first row (rowNumber === 0) on initial load
      if (rowNumber !== undefined && rowNumber >= 0) {
        const rowTop = groupedCumulativeHeights ? groupedCumulativeHeights[rowNumber] : rowNumber * rowHeight
        const rowBottom = rowTop + rowHeight
        // Compare in logical space
        const visibleTop = logicalScrollTop
        const visibleBottom = logicalScrollTop + gridHeight - stickyContentHeight - 5
        if (rowBottom > visibleBottom) {
          scrollToLogical(logicalScrollTop + (rowBottom - visibleBottom))
        } else if (rowTop < visibleTop) {
          scrollToLogical(rowTop)
        }
      }
    }
  }, [selectedCell.rowId, gridHeight])

  // This useeffect looks for changes in the selected column to see whether to scroll left or right
  useEffect(() => {
    const newColumnId = selectedCell.columnId

    if (newColumnId !== '') {
      // Use allColumns to find the column even if it's outside the current viewport
      const column = allColumns.find((column: IViewPortColumn) => column.column.publicId === newColumnId)
      if (column && gridRef.current && !column.isFrozen) {
        const columnWidth = column.column.width
        const columnLeft = column.left
        const columnRight = column.left + columnWidth
        const gridBounds = gridRef.current.getBoundingClientRect()
        const screenLeft = scrollLeft + frozenLeftOffset
        const screenRight = scrollLeft + gridBounds.width

        const rightDifference = columnRight - screenRight
        const leftDifference = columnLeft - screenLeft

        if (rightDifference > 0) {
          horizontalScroll(scrollLeft + rightDifference + 40)
        } else if (leftDifference < 0) {
          horizontalScroll(Math.max(scrollLeft + leftDifference - 40, 0))
        }
      }
    }
  }, [selectedCell.columnId])

  const setCellValue = useCallback(
    (cells: IUpdateTableViewCell, onSuccess: () => void, onError: (error: any) => void) => {
      const payload = viewAdapter.mapCellsForWrite(cells)
      if (!payload) {
        onSuccess()
        return
      }

      updateTableViewCellsAction(
        spreadsheetData.viewDetails.publicId,
        payload,
        spreadsheetData,
        setSpreadsheetData,
        onSuccess,
        onError
      )
    },
    [spreadsheetData.rows, spreadsheetData.viewDetails.columns, viewAdapter.coordinateMap]
  )

  const handleToggleGroup = useCallback((groupKey: string) => {
    setSpreadsheetData({ type: 'TOGGLE_GROUP_COLLAPSE', groupId: groupKey })
  }, [])

  const getChildKeys = (groupedRows: GroupByDictionary, parentKey: string) => {
    let childKeys: string[] = []
    const groupKeys = Object.keys(groupedRows)

    for (let i = 0; i < groupKeys.length; i++) {
      const key = groupKeys[i]
      childKeys.push(`${parentKey}${GROUP_KEY_SPLIT}${key}`)

      const childGroup = groupedRows[key]
      if (
        childGroup &&
        childGroup.childGroups &&
        typeof childGroup.childGroups === 'object' &&
        !Array.isArray(childGroup.childGroups)
      ) {
        const furtherChildKeys = getChildKeys(childGroup.childGroups, `${parentKey}${GROUP_KEY_SPLIT}${key}`)
        childKeys = childKeys.concat(furtherChildKeys)
      }
    }
    return childKeys
  }

  const handleToggleCollapseAll = () => {
    if (groupedRows) {
      const groupKeys = Object.keys(groupedRows)
      let collapsedGroupIds = groupKeys
      for (let i = 0; i < groupKeys.length; i++) {
        const groupKey = groupKeys[i]
        const group = groupedRows[groupKey]
        if (group && group.childGroups && typeof group.childGroups === 'object' && !Array.isArray(group.childGroups)) {
          const childKeys = getChildKeys(group.childGroups, groupKey)
          collapsedGroupIds = collapsedGroupIds.concat(childKeys)
        }
      }
      setSpreadsheetData({ type: 'COLLAPSE_ALL_GROUPS', groupIds: collapsedGroupIds })
    }
  }

  const handleCreateRow = async (values?: Record<string, ICellValue>, nextRowPublicId?: string, fromForm = false) => {
    const processContext = spreadsheetData.processId
      ? {
          processId: spreadsheetData.processId,
          processSectionId: spreadsheetData.processSectionId!
        }
      : undefined

    await runSpreadsheetMutation(
      setSpreadsheetData,
      'Adding row…',
      spreadsheetData.totalRows,
      () =>
        api
          .createNewRow(
            project.publicId,
            spreadsheetData.viewDetails.publicId,
            values ? convertRowDataToNameValueMap(spreadsheetData.viewDetails.columns, values) : {},
            nextRowPublicId,
            processContext
          )
          .then((response) => {
            const apiRowData = response.data[0].rowData as unknown as Record<string, ICellValue>
            const rowDataArray = convertNameMapToRowDataArray(
              spreadsheetData.viewDetails.columns,
              apiRowData,
              spreadsheetData.columnIdToIndex,
              spreadsheetData.indexToColumnId
            )

            let createdRow: ITableRow
            if (spreadsheetData.columnStore) {
              const rowIndex = spreadsheetData.columnStore.addRow()
              for (let i = 0; i < rowDataArray.length; i++) {
                spreadsheetData.columnStore.set(rowIndex, i, rowDataArray[i])
              }
              createdRow = { ...response.data[0], _rowIndex: rowIndex, rowData: undefined }
            } else {
              createdRow = { ...response.data[0], rowData: rowDataArray, _rowIndex: -1 }
            }

            setSpreadsheetData({ type: 'INSERT_ROW', row: createdRow, nextRowPublicId })
          })
          .catch((error) => {
            // Form submits surface their own error (the submit handler's try/catch shows the
            // toast and keeps the form open), so rethrow and let it handle the failure rather
            // than swallowing it here — otherwise the form would report a success it didn't have.
            if (fromForm) throw error
            displayErrorMessage(error)
          }),
      fromForm
    )
  }

  const handleCreateBlankRows = async (numberRows: number, values?: Record<string, ICellValue>) => {
    const processContext = spreadsheetData.processId
      ? {
          processId: spreadsheetData.processId,
          processSectionId: spreadsheetData.processSectionId!
        }
      : undefined

    const label = numberRows === 1 ? 'Adding row…' : 'Adding rows…'
    await runSpreadsheetMutation(setSpreadsheetData, label, spreadsheetData.totalRows, () =>
      api
        .createBlankRows(project.publicId, spreadsheetData.viewDetails.publicId, numberRows, processContext, values)
        .then((response) => {
          if (response.data) {
            const newRows = response.data.map((row) => {
              const apiRowData = row.rowData as unknown as Record<string, ICellValue>
              const rowDataArray = convertNameMapToRowDataArray(
                spreadsheetData.viewDetails.columns,
                apiRowData,
                spreadsheetData.columnIdToIndex,
                spreadsheetData.indexToColumnId
              )

              if (spreadsheetData.columnStore) {
                const rowIndex = spreadsheetData.columnStore.addRow()
                for (let i = 0; i < rowDataArray.length; i++) {
                  spreadsheetData.columnStore.set(rowIndex, i, rowDataArray[i])
                }
                return { ...row, _rowIndex: rowIndex, rowData: undefined } as ITableRow
              }
              return { ...row, rowData: rowDataArray, _rowIndex: -1 } as ITableRow
            })
            setSpreadsheetData({ type: 'APPEND_ROWS', rows: newRows })
          }
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    )
  }

  const clearColumnSelection = (): void => {
    setSelectedColumnIds([])
    setColumnSelectionAnchorId(null)
  }

  // Arms Delete / Copy / Cut on a column selection — the mirror of ``selectCellsForRows``.
  //
  // Unlike rows, the selection is never projected into the cell rectangle: ``setSelectedCell``
  // clears ``selectedColumnIds`` whenever a real cell is written, so a rectangle would destroy the
  // selection it stands for. ``getSelectionTarget`` reads ``selectedColumnIds`` directly instead.
  //
  // Focus is asserted after the clear, not before: clearing the anchor cell calls
  // ``setIsFocused(false)`` as a side effect, and without focus the ``isFocusedRef``-gated copy,
  // cut and Delete handlers do nothing. Ctrl/Cmd+click focuses too, so they work either way.
  const selectCellsForColumns = (columnIdsToSelect: string[]): void => {
    setSelectedCell(INITIAL_SELECTED_CELL)
    setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    setSelectedColumnIds(columnIdsToSelect)
    // The two axes are exclusive, and this can't rely on the document click that normally clears a
    // row selection: a right-click fires contextmenu, not click, and the header's ⋮ button stops
    // propagation — both reach here with rows still selected, and ``getSelectionTarget`` gives rows
    // precedence, so Delete would wipe those rows across every column.
    clearRowSelection()
    if (columnIdsToSelect.length > 0) setIsFocused(true)
  }

  // Whole-column selection: single (plain click), contiguous range (Shift+click from the
  // anchor), or toggled individual columns (Ctrl/Cmd+click). Column and cell selection are
  // mutually exclusive visual modes, so selecting columns clears any cell selection.
  const handleColumnHeaderSelect = (columnId: string, options: { shiftKey: boolean; toggleKey: boolean }): void => {
    const orderedColumnIds = allColumns.map((column) => column.column.publicId)
    const next = computeColumnSelection(
      orderedColumnIds,
      { selectedColumnIds, anchorId: columnSelectionAnchorId },
      columnId,
      options
    )
    setColumnSelectionAnchorId(next.anchorId)
    selectCellsForColumns(next.selectedColumnIds)
  }

  const resetSpreadsheetClick = (event: React.MouseEvent<HTMLElement>) => {
    const element = document.getElementById(`spreadsheet-${uniqueNumber}`)
    if (event.button === 0 && element && element.contains(event.target as Node)) {
      setSelectedCell(INITIAL_SELECTED_CELL)
      setSelectedCellRange(INITIAL_SELECTED_CELL_RANGE)
    }
  }

  // Where the rows are laid out. The grid never scrolls vertically (see helpers/scroll.ts): the
  // rows layer is a zero-height box at the top of the grid, rows are laid out from a base row in
  // a window of at most ROW_BASE_STEP rows (3.3M px at 33px, 13.2M px at the tallest 132px rows,
  // under every browser's coordinate limit), and the layer is translated by `rowsLayerOffset` each frame, so a scroll
  // frame changes one transform rather than every row's `top`. The base moves every
  // ROW_BASE_STEP rows (a style change for the visible rows, rare at scroll speed).
  const rowBase = Math.floor(startRowIndex / ROW_BASE_STEP) * ROW_BASE_STEP
  const rowTopOffset = -rowBase * rowHeight
  const rowsLayerOffset = rowBase * rowHeight - logicalScrollTop
  useLayoutEffect(() => {
    rowBaseOffsetRef.current = rowBase * rowHeight
    maxScrollTopRef.current = maxScrollTop
    logicalScrollTopRef.current = logicalScrollTop
    // The viewport clamps the rendered offset when the table shrinks (a filter); keep the state
    // in step so clearing the filter doesn't jump back to the old offset.
    if (logicalScrollTopState !== logicalScrollTop) setLogicalScrollTopState(logicalScrollTop)
  }, [rowBase, rowHeight, maxScrollTop, logicalScrollTop, logicalScrollTopState])

  // Split context into two parts to avoid scroll-driven re-renders in non-viewport consumers.
  // ViewportContext changes on every scroll frame (~60/sec); DataContext changes on data mutations only.
  const viewportValue = useMemo(
    () => ({
      rows,
      startRowIndex,
      endRowIndex,
      logicalScrollTop,
      logicalTotalHeight,
      maxScrollTop,
      rowsAreaHeight,
      scrollToLogical,
      rowTopOffset,
      rowsLayerOffset,
      rowsLayerRef,
      columns,
      addRowOffsetBase: preViewportAddRowCount * (rowHeight - SMALL_ROW_HEIGHT),
      treeColumnWidth,
      treeLeafColumn,
      handleScroll,
      gridRef,
      attachGrid
    }),
    [
      rows,
      startRowIndex,
      endRowIndex,
      logicalScrollTop,
      logicalTotalHeight,
      maxScrollTop,
      rowsAreaHeight,
      scrollToLogical,
      rowTopOffset,
      rowsLayerOffset,
      columns,
      treeColumnWidth,
      treeLeafColumn,
      preViewportAddRowCount,
      rowHeight,
      handleScroll,
      gridRef,
      attachGrid
    ]
  )

  // Handlers declared as plain functions above get a new identity every render. The context value
  // is memoised on them, so without stable wrappers it would change on every scroll frame and
  // re-render all ~60 consumers (every row and cell among them).
  const stableSelectGroupCell = useStableCallback(selectGroupCell)
  const stableCopyGroupCellValue = useStableCallback(copyGroupCellValue)
  const stableHandleColumnHeaderSelect = useStableCallback(handleColumnHeaderSelect)
  const stableClearColumnSelection = useStableCallback(clearColumnSelection)
  const stableHandleRowNumberSelect = useStableCallback(handleRowNumberSelect)
  const stableClearRowSelection = useStableCallback(clearRowSelection)
  const stableGetSelectedRowIds = useStableCallback(getSelectedRowIds)
  // For consumers that need the count while rendering ("N rows selected"): a stable callback only
  // reaches its latest implementation after commit, so it must not be called during render. A
  // whole-row selection is counted by scanning the rendered rows, so recount only when what the
  // selection reads changes rather than on every provider render (every scroll frame is one).
  const selectedRowCount = useMemo(
    () => getSelectedRowIds().length,
    [
      selectedRowIds,
      selectedColumnIds,
      renderedRows,
      selectedCell,
      selectedCellRange,
      allColumns,
      allVisibleColumnIds,
      treeLeafColumn
    ]
  )
  const stableGetRowIdsToDelete = useStableCallback(getRowIdsToDelete)
  const stableHandleToggleCollapseAll = useStableCallback(handleToggleCollapseAll)
  const stableHandleCreateRow = useStableCallback(handleCreateRow)
  const stableHandleCreateBlankRows = useStableCallback(handleCreateBlankRows)
  const stableCutSelectedCells = useStableCallback(cutSelectedCells)
  const stableResetSpreadsheetClick = useStableCallback(resetSpreadsheetClick)

  const contextValue = useMemo(
    () => ({
      spreadsheetData: effectiveSpreadsheetData,
      setSpreadsheetData,
      selectedCell,
      setSelectedCell,
      selectedGroupCell,
      selectGroupCell: stableSelectGroupCell,
      copyGroupCellValue: stableCopyGroupCellValue,
      selectedCellRange,
      setSelectedCellRange,
      selectedColumnIds,
      handleColumnHeaderSelect: stableHandleColumnHeaderSelect,
      clearColumnSelection: stableClearColumnSelection,
      pendingRenameColumnId,
      setPendingRenameColumnId,
      selectedRowIds,
      handleRowNumberSelect: stableHandleRowNumberSelect,
      clearRowSelection: stableClearRowSelection,
      getSelectedRowIds: stableGetSelectedRowIds,
      selectedRowCount,
      getRowIdsToDelete: stableGetRowIdsToDelete,
      handleKeyDown,
      handleKeyDownForm,
      uniqueNumber,
      setCellValue,
      groupedRows,
      allColumns,
      totalWidth,
      handleToggleGroup,
      handleToggleCollapseAll: stableHandleToggleCollapseAll,
      handleCreateRow: stableHandleCreateRow,
      handleCreateBlankRows: stableHandleCreateBlankRows,
      setIsFocused,
      filterMenu,
      setFilterMenu,
      colourMenu,
      setColourMenu,
      groupMenu,
      setGroupMenu,
      showToolbar: props.showToolbar !== undefined ? props.showToolbar : true,
      showAggregate,
      fitToHeight: props.fitToHeight ?? false,
      handlePasteText,
      cutSelectedCells: stableCutSelectedCells,
      expandedRowsWithGroups,
      filterSearchRows,
      setFilterSearchRows,
      inProcess,
      originalViewId: props.tableViewId ? props.tableViewId : undefined,
      updateGridDimensions: updateDimensions,
      resetSpreadsheetClick: stableResetSpreadsheetClick,
      setIsDragging,
      fillDragState,
      startFillDrag,
      fillDownToData,
      treeLeafColumn,
      treeColumnWidth
    }),
    [
      effectiveSpreadsheetData,
      selectedCell,
      selectedGroupCell,
      stableSelectGroupCell,
      stableCopyGroupCellValue,
      selectedCellRange,
      selectedColumnIds,
      stableHandleColumnHeaderSelect,
      stableClearColumnSelection,
      pendingRenameColumnId,
      selectedRowIds,
      stableHandleRowNumberSelect,
      stableClearRowSelection,
      stableGetSelectedRowIds,
      selectedRowCount,
      stableGetRowIdsToDelete,
      groupedRows,
      allColumns,
      totalWidth,
      filterMenu,
      colourMenu,
      groupMenu,
      expandedRowsWithGroups,
      filterSearchRows,
      fillDragState,
      props.showToolbar,
      props.showAggregate,
      props.fitToHeight,
      // Stable references (functions created with useCallback or from useState)
      setSpreadsheetData,
      setSelectedCell,
      setSelectedCellRange,
      handleKeyDown,
      handleKeyDownForm,
      uniqueNumber,
      setCellValue,
      handleToggleGroup,
      stableHandleToggleCollapseAll,
      stableHandleCreateRow,
      stableHandleCreateBlankRows,
      setIsFocused,
      setFilterMenu,
      setColourMenu,
      setGroupMenu,
      handlePasteText,
      stableCutSelectedCells,
      setFilterSearchRows,
      inProcess,
      updateDimensions,
      stableResetSpreadsheetClick,
      setIsDragging,
      startFillDrag,
      fillDownToData,
      treeLeafColumn,
      treeColumnWidth
    ]
  )

  // When unmounting, render null children to release all child component references
  // This helps garbage collection by removing the React fiber tree for children
  if (isUnmounting) {
    return (
      <ViewportContext.Provider value={viewportValue}>
        <DataContext.Provider value={contextValue}>{null}</DataContext.Provider>
      </ViewportContext.Provider>
    )
  }

  return (
    <ViewportContext.Provider value={viewportValue}>
      <DataContext.Provider value={contextValue}>{props.children}</DataContext.Provider>
    </ViewportContext.Provider>
  )
}
